import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
  Dialog,
  Button,
  Checkbox,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ContentCopy as ContentCopyIcon,
  Shortcut as ForwardIcon,
  PushPin as PushPinIcon,
  MarkChatUnread as MarkChatUnreadIcon,
  MarkChatRead as MarkChatReadIcon,
  Mic as MicIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Reply as ReplyIcon,
  Chat as ChatIcon,
  MoreVert as MoreVertIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ChevronRight as ChevronRightIcon,
  Done as DoneIcon,
  ErrorOutline as ErrorOutlineIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  DeleteForever as DeleteForeverIcon,
  Bolt as BoltIcon,
} from "@mui/icons-material";
import io from "socket.io-client";
import moment from "moment";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import PageShell from "components/bento/PageShell";
import chatBackground from "assets/chat-bg-tile.png";
import MediaComposer from "./MediaComposer";
import ChatMediaPanel, { loadRecentMedia } from "./ChatMediaPanel";
import {
  QuickReplyPicker,
  QuickReplyEditDialog,
  QuickReplySendDialog,
  filterQuickReplies,
  fillQuickReply,
  quickReplyText,
  toQrLang,
} from "./QuickReplies";
import {
  FONT,
  TEXT_DARK,
  TEXT_SECONDARY,
  TEXT_SUBTLE,
  BG_TINT,
  BORDER,
  RADIUS_CARD,
  PURPLE_MAIN,
  PURPLE_TINT_100,
} from "components/bento/bentoTokens";
import { getGroupStatus } from "api/whatsapp";
import {
  getInboxChats,
  getInboxChatMessages,
  sendInboxChatMessage,
  sendInboxChatMedia,
  sendInboxChatMediaBatch,
  getInboxChatMediaUrl,
  markInboxChatRead,
  getInboxChatProfilePic,
  getInboxChatInfo,
  forwardInboxMessages,
  markInboxChatUnread,
  setInboxChatPinned,
  editInboxChatMessage,
  revokeInboxChatMessage,
  getQuickReplies,
  saveQuickReply,
  deleteQuickReply,
  sendQuickReply,
} from "api/whatsapp";
import Swal from "sweetalert2";

const avatarInitial = (name) => (name || "?").trim().charAt(0).toUpperCase() || "?";

const CHAT_PAGE_SIZE = 50;
const CHAT_FILTERS = ["all", "unread", "favorites", "groups"];

// Profile photos: one shared promise per chat (so the list, header and info panel reuse a
// single request) and at most 4 requests in flight, so opening a list of ~100 chats doesn't
// fire 100 WhatsApp lookups at once. The backend caches too.
const profilePicCache = new Map(); // `${groupId}|${chatId}` -> Promise<string|null>
const PROFILE_PIC_CONCURRENCY = 4;
let profilePicActive = 0;
const profilePicQueue = [];
const onProfilePicDone = () => {
  profilePicActive -= 1;
  // eslint-disable-next-line no-use-before-define
  runNextProfilePic();
};
function runNextProfilePic() {
  while (profilePicActive < PROFILE_PIC_CONCURRENCY && profilePicQueue.length) {
    const job = profilePicQueue.shift();
    profilePicActive += 1;
    job().finally(onProfilePicDone);
  }
}
// The photo itself is the same regardless of which mount fetched it, so the cache key doesn't
// need apiBase — only the request to get there does.
const loadProfilePic = (groupId, chatId, apiBase) => {
  const key = `${groupId}|${chatId}`;
  if (!profilePicCache.has(key)) {
    profilePicCache.set(
      key,
      new Promise((resolve) => {
        profilePicQueue.push(() =>
          getInboxChatProfilePic(groupId, chatId, apiBase).then((r) => resolve(r.success ? r.data || null : null))
        );
        runNextProfilePic();
      })
    );
  }
  return profilePicCache.get(key);
};

// Opening a chat re-checks that one chat's photo with WhatsApp (the backend throttles it) and
// pushes a changed photo to every avatar of that chat on screen.
const profilePicListeners = new Set();
const refreshProfilePic = (groupId, chatId, apiBase) => {
  const key = `${groupId}|${chatId}`;
  getInboxChatProfilePic(groupId, chatId, apiBase, { refresh: true }).then((r) => {
    if (!r.success) return;
    const url = r.data || null;
    profilePicCache.set(key, Promise.resolve(url));
    profilePicListeners.forEach((listener) => listener(key, url));
  });
};

// `lazy` defers the photo lookup until the avatar scrolls into view — used for group member
// lists, where fetching every member's photo up front would mean one WhatsApp call each.
const ChatAvatar = ({ groupId, apiBase = "settings", chat, size = 40, lazy = false }) => {
  const [url, setUrl] = useState(null);
  const [visible, setVisible] = useState(!lazy);
  const ref = useRef(null);

  useEffect(() => {
    if (!lazy || visible || !ref.current || typeof IntersectionObserver === "undefined") {
      if (lazy && typeof IntersectionObserver === "undefined") setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [lazy, visible]);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    if (chat?.id && visible) {
      loadProfilePic(groupId, chat.id, apiBase).then((u) => {
        if (!cancelled) setUrl(u);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [groupId, apiBase, chat?.id, visible]);

  useEffect(() => {
    const key = `${groupId}|${chat?.id}`;
    const listener = (changedKey, newUrl) => {
      if (changedKey === key) setUrl(newUrl);
    };
    profilePicListeners.add(listener);
    return () => profilePicListeners.delete(listener);
  }, [groupId, chat?.id]);

  return (
    <Avatar
      ref={ref}
      src={url || undefined}
      imgProps={{ onError: () => setUrl(null) }}
      // Neutral gray (like WhatsApp's default avatar) — only visible when there's no photo.
      sx={{ width: size, height: size, bgcolor: "#c9ced3", color: "#fff", fontSize: size * 0.42 }}
    >
      {chat?.isGroup ? <GroupIcon sx={{ fontSize: size * 0.5 }} /> : avatarInitial(chat?.name)}
    </Avatar>
  );
};

// A group member's (or message sender's) profile inside the info panel: photo, name, number,
// about — and "Message" to open a 1:1 chat with them.
const MemberProfile = ({ groupId, apiBase, member, onBack, onClose, onOpenImage, onMessage, t }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    setLoading(true);
    getInboxChatInfo(groupId, member.id, apiBase).then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (r.success) setInfo(r.data);
    });
    return () => {
      cancelled = true;
    };
  }, [groupId, apiBase, member.id]);

  const number = info?.number || member.number;
  const name = info?.name || member.name || info?.pushname || (number ? `+${number}` : member.id.split("@")[0]);

  return (
    <Box sx={{ width: 340, flexShrink: 0, display: "flex", flexDirection: "column", borderLeft: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
        <IconButton size="small" color="secondary" onClick={onBack || onClose} aria-label={t("whatsappChat.back")}>
          {onBack ? <ArrowBackIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
        </IconButton>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: TEXT_DARK, flex: 1 }}>{t("whatsappChat.contactInfo")}</Typography>
        {onBack && (
          <IconButton size="small" color="secondary" onClick={onClose} aria-label={t("common.close")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", px: 2, py: 3 }}>
          <Box
            onClick={() => info?.profilePicUrl && onOpenImage({ src: info.profilePicUrl })}
            sx={{ cursor: info?.profilePicUrl ? "zoom-in" : "default" }}
          >
            <ChatAvatar groupId={groupId} apiBase={apiBase} chat={{ id: member.id, name }} size={140} />
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: TEXT_DARK, mt: 1.5, wordBreak: "break-word" }}>{name}</Typography>
          {number && name !== `+${number}` && (
            <Typography sx={{ fontFamily: FONT, fontSize: 14, color: TEXT_SECONDARY, mt: 0.5 }}>+{number}</Typography>
          )}
          {info?.pushname && info.pushname !== name && (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_SUBTLE, mt: 0.25 }}>~{info.pushname}</Typography>
          )}
          {info?.isBusiness && (
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: PURPLE_MAIN, mt: 0.5, fontWeight: 600 }}>
              {t("whatsappChat.businessAccount")}
            </Typography>
          )}
          {loading && <CircularProgress size={20} color="secondary" sx={{ mt: 1.5 }} />}
          {!member.isMe && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ChatIcon />}
              onClick={() => onMessage(member, info)}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {t("whatsappChat.messageMember")}
            </Button>
          )}
        </Box>
        {info?.about && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_SECONDARY, mb: 0.75 }}>{t("whatsappChat.about")}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 14, color: TEXT_DARK, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{info.about}</Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

// Right-hand "Contact info" / "Group info" panel, like WhatsApp Web's. Media/docs are taken
// from the messages already loaded for this chat (the most recent ones). In a group, a member
// (or `initialMember` — a message sender that was clicked) opens their own profile.
const ContactInfoPanel = ({ groupId, apiBase = "settings", chat, initialMember = null, onClose, onOpenImage, onOpenMedia, onMessageMember, t }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [member, setMember] = useState(initialMember);
  useEffect(() => {
    setMember(initialMember);
  }, [initialMember, chat.id]);

  const filteredMembers = useMemo(() => {
    const members = (info && info.members) || [];
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (mem) => (mem.name || "").toLowerCase().includes(q) || (mem.number || "").includes(q.replace(/\D/g, "") || q)
    );
  }, [info, memberSearch]);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    setMemberSearch("");
    setLoading(true);
    getInboxChatInfo(groupId, chat.id, apiBase).then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (r.success) setInfo(r.data);
    });
    return () => {
      cancelled = true;
    };
  }, [groupId, apiBase, chat.id]);

  // Recent photos / videos of the whole chat (not just the loaded page) for the strip.
  const [recentMedia, setRecentMedia] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setRecentMedia(null);
    loadRecentMedia(groupId, chat.id, apiBase).then((items) => {
      if (!cancelled) setRecentMedia(items.slice(0, 6));
    });
    return () => {
      cancelled = true;
    };
  }, [groupId, apiBase, chat.id]);

  const sectionTitleSx = { fontFamily: FONT, fontSize: 13, color: TEXT_SECONDARY, mb: 0.75 };
  const displayName = info?.name || info?.pushname || chat.name;

  if (member) {
    return (
      <MemberProfile
        groupId={groupId}
        apiBase={apiBase}
        member={member}
        // Opened straight from a sender's name → ✕ only; from the member list → back to it.
        onBack={initialMember && member === initialMember ? null : () => setMember(null)}
        onClose={onClose}
        onOpenImage={onOpenImage}
        onMessage={onMessageMember}
        t={t}
      />
    );
  }

  return (
    <Box sx={{ width: 340, flexShrink: 0, display: "flex", flexDirection: "column", borderLeft: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
        <IconButton size="small" color="secondary" onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: TEXT_DARK }}>
          {chat.isGroup ? t("whatsappChat.groupInfo") : t("whatsappChat.contactInfo")}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", px: 2, py: 3 }}>
          <Box
            onClick={() => info?.profilePicUrl && onOpenImage({ src: info.profilePicUrl })}
            sx={{ cursor: info?.profilePicUrl ? "zoom-in" : "default" }}
          >
            <ChatAvatar groupId={groupId} apiBase={apiBase} chat={chat} size={140} />
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: TEXT_DARK, mt: 1.5, wordBreak: "break-word" }}>
            {displayName}
          </Typography>
          {!chat.isGroup && info?.number && (
            <Typography sx={{ fontFamily: FONT, fontSize: 14, color: TEXT_SECONDARY, mt: 0.5 }}>+{info.number}</Typography>
          )}
          {!chat.isGroup && info?.pushname && info?.name && info.pushname !== info.name && (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_SUBTLE, mt: 0.25 }}>~{info.pushname}</Typography>
          )}
          {chat.isGroup && info?.members && (
            <Typography sx={{ fontFamily: FONT, fontSize: 14, color: TEXT_SECONDARY, mt: 0.5 }}>
              {t("whatsappChat.groupMemberSummary", { count: info.members.length })}
            </Typography>
          )}
          {info?.isBusiness && (
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: PURPLE_MAIN, mt: 0.5, fontWeight: 600 }}>
              {t("whatsappChat.businessAccount")}
            </Typography>
          )}
          {loading && <CircularProgress size={20} color="secondary" sx={{ mt: 1.5 }} />}
        </Box>

        {(info?.about || info?.description) && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={sectionTitleSx}>{chat.isGroup ? t("whatsappChat.description") : t("whatsappChat.about")}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 14, color: TEXT_DARK, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {chat.isGroup ? info.description : info.about}
              </Typography>
            </Box>
          </>
        )}

        {chat.isGroup && info && (info.createdAt || info.createdBy || info.onlyAdminsCanSend) && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              {(info.createdAt || info.createdBy) && (
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_SECONDARY }}>
                  {info.createdBy
                    ? t("whatsappChat.createdByOn", {
                        name: info.createdBy.name || (info.createdBy.number ? `+${info.createdBy.number}` : "—"),
                        date: info.createdAt ? moment.unix(info.createdAt).format("DD/MM/YYYY HH:mm") : "—",
                      })
                    : t("whatsappChat.createdOn", { date: moment.unix(info.createdAt).format("DD/MM/YYYY HH:mm") })}
                </Typography>
              )}
              {info.onlyAdminsCanSend && (
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_SECONDARY, mt: 0.5 }}>
                  🔒 {t("whatsappChat.onlyAdminsCanSend")}
                </Typography>
              )}
            </Box>
          </>
        )}

        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          {/* Media, links and docs → the full panel (tabs, grouped by month). */}
          <Box
            onClick={onOpenMedia}
            sx={{ display: "flex", alignItems: "center", cursor: "pointer", mb: 0.75, "&:hover .wa-media-more": { color: PURPLE_MAIN } }}
          >
            <Typography sx={{ ...sectionTitleSx, mb: 0, flex: 1 }}>{t("whatsappChat.mediaLinksDocs")}</Typography>
            <ChevronRightIcon className="wa-media-more" fontSize="small" sx={{ color: TEXT_SUBTLE }} />
          </Box>
          {recentMedia === null ? (
            <CircularProgress size={18} color="secondary" />
          ) : recentMedia.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_SUBTLE }}>{t("whatsappChat.mediaEmpty.media")}</Typography>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.75 }}>
              {recentMedia.map((m) => {
                const url = getInboxChatMediaUrl(groupId, chat.id, m.id, apiBase);
                const thumb = m.thumbnail ? `data:image/jpeg;base64,${m.thumbnail}` : null;
                return (
                  <Box
                    key={m.id}
                    component="img"
                    src={m.mediaType === "image" ? url : thumb || url}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      if (thumb && e.currentTarget.src !== thumb) e.currentTarget.src = thumb;
                    }}
                    onClick={() =>
                      m.mediaType === "image" ? onOpenImage({ src: url, fallback: thumb, filename: m.filename, messageId: m.id }) : onOpenMedia()
                    }
                    sx={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 1, bgcolor: BG_TINT, cursor: "pointer" }}
                  />
                );
              })}
            </Box>
          )}
        </Box>

        {chat.isGroup && info?.members && info.members.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={sectionTitleSx}>{t("whatsappChat.memberCount", { count: info.members.length })}</Typography>
              {info.members.length > 8 && (
                <TextField
                  size="small"
                  fullWidth
                  color="secondary"
                  placeholder={t("whatsappChat.searchMembers")}
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: TEXT_SUBTLE }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {filteredMembers.map((mem) => (
                <Box
                  key={mem.id}
                  onClick={mem.isMe ? undefined : () => setMember(mem)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    py: 0.75,
                    px: 0.75,
                    mx: -0.75,
                    borderRadius: 1.5,
                    cursor: mem.isMe ? "default" : "pointer",
                    "&:hover": mem.isMe ? undefined : { bgcolor: BG_TINT },
                  }}
                >
                  <ChatAvatar groupId={groupId} apiBase={apiBase} chat={{ id: mem.id, name: mem.name || mem.number }} size={36} lazy />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontFamily: FONT, fontSize: 14, color: TEXT_DARK }}>
                      {mem.isMe
                        ? t("whatsappChat.you")
                        : mem.name || (mem.number ? `+${mem.number}` : mem.id.split("@")[0])}
                    </Typography>
                    {!mem.isMe && mem.name && mem.number && (
                      <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_SUBTLE }}>+{mem.number}</Typography>
                    )}
                  </Box>
                  {mem.isAdmin && (
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: 11,
                        color: PURPLE_MAIN,
                        fontWeight: 600,
                        border: `1px solid ${PURPLE_TINT_100}`,
                        bgcolor: PURPLE_TINT_100,
                        borderRadius: 1,
                        px: 0.75,
                        py: 0.25,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {mem.isOwner ? t("whatsappChat.groupOwner") : t("whatsappChat.groupAdmin")}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

// "No LID for user" is a known, still-open upstream whatsapp-web.js/WhatsApp-side bug
// (github.com/wwebjs/whatsapp-web.js issue #3834) — WhatsApp hasn't synced a "Linked ID" for
// this specific contact yet, usually because this WhatsApp number has never/rarely messaged
// them before. There's no fix available in the library yet, and the known workarounds risk
// getting the account disconnected (the exact self-heal issue fixed elsewhere in this app),
// so this is surfaced as a clear per-contact limitation instead of a generic send error.
const isNoLidError = (message) => /no lid for user/i.test(message || "");

// Message text with clickable links (http(s):// and bare www.). Trailing punctuation
// ("see https://x.com/a.") stays out of the link.
const URL_RE = /((?:https?:\/\/|www\.)[^\s<>]+)/gi;

// WhatsApp text formatting: _italic_, *bold*, ~strikethrough~. Like WhatsApp, the markers must
// hug the text and sit at a word edge, so "snake_case_name", "2 * 3 * 4" or a line of
// underscores stay as typed.
const FORMAT_RE = /(^|[^A-Za-z0-9_*~])(?:_([^\s_](?:[^_\n]*[^\s_])?)_|\*([^\s*](?:[^*\n]*[^\s*])?)\*|~([^\s~](?:[^~\n]*[^\s~])?)~)(?![A-Za-z0-9])/g;
// Formats nest ("_*both*_" → bold italic): the inside of each match is formatted again.
const formatText = (text, keyPrefix) => {
  // Collect the matches first — the recursion below reuses the same (stateful) regex.
  const matches = [];
  FORMAT_RE.lastIndex = 0;
  for (let m = FORMAT_RE.exec(text); m; m = FORMAT_RE.exec(text)) {
    const Tag = m[2] != null ? "em" : m[3] != null ? "strong" : "s";
    matches.push({ start: m.index + m[1].length, end: FORMAT_RE.lastIndex, inner: m[2] ?? m[3] ?? m[4], Tag });
  }
  const out = [];
  let last = 0;
  for (const { start, end, inner, Tag } of matches) {
    if (start > last) out.push(text.slice(last, start));
    const key = `${keyPrefix}-${start}`;
    out.push(<Tag key={key}>{formatText(inner, key)}</Tag>);
    last = end;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const linkify = (text) =>
  String(text)
    .split(URL_RE)
    .flatMap((part, i) => {
      if (!part) return [];
      if (i % 2 === 0) return formatText(part, i);
      const tail = (part.match(/[.,!?;:)\]}'"]+$/) || [""])[0];
      const link = tail ? part.slice(0, -tail.length) : part;
      return [
        <a
          key={i}
          href={/^www\./i.test(link) ? `https://${link}` : link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          {link}
        </a>,
        tail,
      ];
    });

// WhatsApp's own link preview (image · title · description) for a text message — opens the link.
const LinkPreview = ({ preview, fromMe }) => (
  <Box
    component="a"
    href={/^https?:\/\//i.test(preview.url) ? preview.url : `https://${preview.url}`}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: "block",
      maxWidth: 320,
      mb: 0.5,
      borderRadius: 1,
      overflow: "hidden",
      textDecoration: "none",
      color: "inherit",
      bgcolor: fromMe ? "rgba(0,0,0,.15)" : BG_TINT,
    }}
  >
    {preview.thumbnail && (
      <Box
        component="img"
        src={`data:image/jpeg;base64,${preview.thumbnail}`}
        alt=""
        sx={{ display: "block", width: "100%", maxHeight: 180, objectFit: "cover" }}
      />
    )}
    {(preview.title || preview.description) && (
      <Box sx={{ px: 1, py: 0.75 }}>
        {preview.title && (
          <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{preview.title}</Typography>
        )}
        {preview.description && (
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: 12,
              opacity: 0.8,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {preview.description}
          </Typography>
        )}
      </Box>
    )}
  </Box>
);

// ── Replies ──────────────────────────────────────────────────────────────────────────────
// A reply's `quoted` (from the backend) and the reply bar's target share one shape:
// { id, fromMe, author, authorName, authorNumber, authorPushname, type, body, thumbnail }.
const quoteOf = (m) => ({
  id: m.id,
  fromMe: !!m.fromMe,
  author: m.author,
  authorName: m.authorName,
  authorNumber: m.authorNumber,
  authorPushname: m.authorPushname,
  type: m.hasMedia ? m.mediaType : "chat",
  body: m.hasMedia && m.body === m.filename ? "" : m.body,
  thumbnail: m.thumbnail || null,
});

// Our own "sent by" footer (backend senderFooter) is noise inside a quote — drop it.
const FOOTER_RE = /\n?_{8,}\n_sent by [^\n]*_\s*$/;

// WhatsApp's own limits for changing a sent message (the backend has the final say).
const EDIT_WINDOW_S = 15 * 60;
const REVOKE_WINDOW_S = 2 * 24 * 60 * 60;
const canEditMsg = (m) =>
  !!m?.id && m.fromMe && !m.revoked && !m.hasMedia && !!m.body && moment().unix() - (m.timestamp || 0) < EDIT_WINDOW_S;
const canRevokeMsg = (m) => !!m?.id && m.fromMe && !m.revoked && moment().unix() - (m.timestamp || 0) < REVOKE_WINDOW_S;
const quotedText = (q, t) => {
  const body = String(q.body || "").replace(FOOTER_RE, "").trim();
  if (body) return body;
  return q.type && q.type !== "chat" ? t(`whatsappChat.mediaLabels.${q.type}`, { defaultValue: q.type }) : "";
};
const quotedName = (q, chat, t) => {
  if (q.fromMe) return t("whatsappChat.you");
  if (chat?.isGroup) return q.authorName || (q.authorNumber ? `+${q.authorNumber}` : q.authorPushname ? `~${q.authorPushname}` : q.author || "");
  return chat?.name || "";
};
// Same message? Ids can differ in the chat part (…@lid vs …@c.us) for the same message, so
// fall back to the message's own key (3rd "_" part of "<fromMe>_<chat>_<key>[_<participant>]").
const msgKey = (id) => String(id || "").split("_")[2] || id;
const sameMessage = (a, b) => !!a && !!b && (a === b || msgKey(a) === msgKey(b));

// The quoted message shown at the top of a reply bubble (and in the reply bar above the input).
const QuotedBlock = ({ q, chat, onBubble = false, fromMe = false, onClick, t }) => {
  const thumb = q.thumbnail ? `data:image/jpeg;base64,${q.thumbnail}` : null;
  const inOwnBubble = onBubble && fromMe;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "stretch",
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: inOwnBubble ? "rgba(0,0,0,.18)" : "rgba(145,95,202,.08)",
        borderLeft: `4px solid ${inOwnBubble ? "rgba(255,255,255,.9)" : PURPLE_MAIN}`,
        cursor: onClick ? "pointer" : "default",
        minWidth: 0,
        flex: onBubble ? undefined : 1,
        mb: onBubble ? 0.5 : 0,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, px: 1, py: 0.5 }}>
        <Typography
          noWrap
          sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: inOwnBubble ? "#fff" : PURPLE_MAIN }}
        >
          {quotedName(q, chat, t)}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: 12.5,
            color: inOwnBubble ? "rgba(255,255,255,.9)" : TEXT_SECONDARY,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {quotedText(q, t)}
        </Typography>
      </Box>
      {thumb && <Box component="img" src={thumb} alt="" sx={{ width: 52, minHeight: 52, objectFit: "cover", flexShrink: 0 }} />}
    </Box>
  );
};

// Renders one message's attachment by WhatsApp message type. Full media streams from the
// /media endpoint; image/video/sticker messages also carry WhatsApp Web's own inline base64
// preview (`thumbnail`), which is shown instantly and used as the fallback when the full
// download fails (the CDN download is the fragile part — the preview is already in hand).
const MediaContent = ({ m, groupId, apiBase = "settings", chatId, t, onOpenImage }) => {
  const [fullFailed, setFullFailed] = useState(false);
  const url = m.id ? getInboxChatMediaUrl(groupId, chatId, m.id, apiBase) : null;
  const thumbSrc = m.thumbnail ? `data:image/jpeg;base64,${m.thumbnail}` : null;
  const type = m.mediaType;
  const textColor = m.fromMe ? "#fff" : PURPLE_MAIN;
  const mutedColor = m.fromMe ? "rgba(255,255,255,.85)" : TEXT_SUBTLE;

  const unavailable = (
    <Typography sx={{ fontFamily: FONT, fontSize: 13, fontStyle: "italic", color: mutedColor, px: 1, pt: 0.5 }}>
      📎 {t("whatsappChat.mediaUnavailable", { type: type || "media" })}
    </Typography>
  );

  if (type === "sticker") {
    const src = !fullFailed && url ? url : thumbSrc;
    if (!src) return unavailable;
    return (
      <Box
        component="img"
        src={src}
        alt="sticker"
        loading="lazy"
        onError={() => setFullFailed(true)}
        sx={{ width: 140, height: 140, objectFit: "contain", display: "block" }}
      />
    );
  }

  if (type === "image") {
    const src = !fullFailed && url ? url : thumbSrc;
    if (!src) return unavailable;
    // Fixed WhatsApp-Web-style frame so every photo bubble is the same size regardless of the
    // image's own dimensions; click opens the full-size viewer.
    return (
      <Box
        component="img"
        src={src}
        alt="image"
        loading="lazy"
        onError={() => setFullFailed(true)}
        onClick={() => onOpenImage({ src: !fullFailed && url ? url : thumbSrc, fallback: thumbSrc, filename: m.filename, messageId: m.id })}
        sx={{
          width: "100%",
          aspectRatio: "1 / 1",
          objectFit: "cover",
          borderRadius: 1.5,
          display: "block",
          cursor: "zoom-in",
          bgcolor: BG_TINT,
          mb: m.body ? 0.5 : 0,
        }}
      />
    );
  }

  if (type === "ptt" || type === "audio") {
    if (!url || fullFailed) return unavailable;
    // WhatsApp's own recorded length, shown immediately (the player only learns the duration
    // once its metadata has loaded).
    const seconds = Number(m.duration) || 0;
    const durationLabel = seconds ? `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}` : null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          component="audio"
          controls
          preload="metadata"
          src={url}
          onError={() => setFullFailed(true)}
          sx={{ display: "block", width: 260, maxWidth: "100%", height: 40 }}
        />
        {durationLabel && (
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: mutedColor, whiteSpace: "nowrap" }}>
            {type === "ptt" ? "🎤 " : ""}
            {durationLabel}
          </Typography>
        )}
      </Box>
    );
  }

  if (type === "video") {
    if (!url && !thumbSrc) return unavailable;
    if (fullFailed || !url) {
      return thumbSrc ? (
        <Box component="img" src={thumbSrc} alt="video" sx={{ maxWidth: "100%", maxHeight: 260, borderRadius: 1, display: "block" }} />
      ) : (
        unavailable
      );
    }
    return (
      <Box
        component="video"
        controls
        preload="none"
        poster={thumbSrc || undefined}
        src={url}
        onError={() => setFullFailed(true)}
        sx={{ maxWidth: "100%", maxHeight: 260, borderRadius: 1, display: "block", mb: m.body ? 0.5 : 0 }}
      />
    );
  }

  // document, and anything else with a file behind it
  if (!url) return unavailable;
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noreferrer"
      download={m.filename || undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontFamily: FONT,
        fontSize: 13,
        color: textColor,
        textDecoration: "none",
        border: `1px solid ${m.fromMe ? "rgba(255,255,255,.35)" : BORDER}`,
        borderRadius: 1.5,
        px: 1,
        py: 0.75,
        mb: m.body ? 0.5 : 0,
      }}
    >
      <InsertDriveFileIcon fontSize="small" />
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
        {m.filename || type || t("whatsappChat.attachment")}
      </Box>
    </Box>
  );
};

// Shown in the viewer when neither the full image nor WhatsApp's inline preview loads.
const VIEWER_ERROR_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="160" viewBox="0 0 240 160"><rect width="240" height="160" rx="12" fill="#2a2438"/><text x="120" y="88" text-anchor="middle" font-family="sans-serif" font-size="40" fill="#8a8299">⚠</text></svg>'
  );

// Full-size photo viewer, like WhatsApp's: every photo in the open chat, swipe / arrow keys to
// move between them, wheel / pinch / double-click to zoom and drag to pan. `viewer` is
// { slides: [{ src, fallback, filename }], index }. A photo whose full file fails to load drops
// to WhatsApp's inline preview (`fallback`).
const ImageViewer = ({ viewer, onClose }) => {
  const [index, setIndex] = useState(0);
  const [srcOverride, setSrcOverride] = useState({}); // slide index -> src to use instead
  useEffect(() => {
    setSrcOverride({});
    setIndex(viewer?.index || 0);
  }, [viewer]);

  const slides = useMemo(
    () => (viewer?.slides || []).map((s, i) => ({ ...s, src: srcOverride[i] || s.src, slideIndex: i })),
    [viewer, srcOverride]
  );
  const current = slides[index];
  const multiple = slides.length > 1;

  return (
    <Lightbox
      open={!!viewer}
      close={onClose}
      index={index}
      slides={slides}
      plugins={multiple ? [Zoom, Counter] : [Zoom]}
      on={{ view: ({ index: i }) => setIndex(i) }}
      zoom={{ maxZoomPixelRatio: 4, scrollToZoom: true }}
      carousel={{
        finite: true,
        imageProps: (slide) => ({
          onError: () =>
            setSrcOverride((prev) => {
              const base = viewer?.slides?.[slide.slideIndex];
              if (!base) return prev;
              const cur = prev[slide.slideIndex] || base.src;
              if (base.fallback && cur !== base.fallback) return { ...prev, [slide.slideIndex]: base.fallback };
              if (cur !== VIEWER_ERROR_IMG) return { ...prev, [slide.slideIndex]: VIEWER_ERROR_IMG };
              return prev;
            }),
        }),
      }}
      controller={{ closeOnBackdropClick: true }}
      toolbar={{
        buttons: [
          current && current.src && !current.src.startsWith("data:") ? (
            <IconButton
              key="download"
              component="a"
              href={current.src}
              download={current.filename || true}
              sx={{ color: "#fff" }}
            >
              <DownloadIcon />
            </IconButton>
          ) : null,
          "close",
        ].filter(Boolean),
      }}
      render={multiple ? undefined : { buttonPrev: () => null, buttonNext: () => null }}
      styles={{ container: { backgroundColor: "rgba(12,10,20,.94)" } }}
    />
  );
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Picked attachment is previewed first (like WhatsApp Web) and only sent on "Send".
// Images and PDFs render inline; other documents show name + size.
const AttachmentPreview = ({ file, sending, error, onCancel, onSend, t }) => {
  const [url, setUrl] = useState(null);
  const kind = file?.type?.startsWith("image/") ? "image" : file?.type === "application/pdf" ? "pdf" : "file";

  useEffect(() => {
    if (!file || kind === "file") {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, kind]);

  return (
    <Dialog open={!!file} onClose={sending ? undefined : onCancel} maxWidth="md" fullWidth>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}` }}>
        <IconButton onClick={onCancel} disabled={sending} aria-label={t("common.cancel")}>
          <CloseIcon />
        </IconButton>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: TEXT_DARK, flex: 1 }} noWrap>
          {file?.name}
        </Typography>
        <Typography sx={{ fontFamily: FONT, color: TEXT_SUBTLE, fontSize: 13 }}>{formatFileSize(file?.size)}</Typography>
      </Box>
      <Box
        sx={{
          bgcolor: BG_TINT,
          height: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: kind === "pdf" ? 0 : 3,
        }}
      >
        {kind === "image" && url && (
          <Box component="img" src={url} alt={file?.name} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 1 }} />
        )}
        {kind === "pdf" && url && (
          <Box component="iframe" src={url} title={file?.name} sx={{ width: "100%", height: "100%", border: 0 }} />
        )}
        {kind === "file" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <InsertDriveFileIcon sx={{ fontSize: 72, color: TEXT_SUBTLE }} />
            <Typography sx={{ fontFamily: FONT, color: TEXT_DARK, fontWeight: 600 }}>{file?.name}</Typography>
            <Typography sx={{ fontFamily: FONT, color: TEXT_SUBTLE, fontSize: 13 }}>
              {t("whatsappChat.noPreview")}
            </Typography>
          </Box>
        )}
      </Box>
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 2, py: 1.5, borderTop: `1px solid ${BORDER}` }}>
        <Button variant="outlined" color="secondary" onClick={onCancel} disabled={sending}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={onSend}
          disabled={sending}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          {sending ? t("whatsappChat.sending") : t("whatsappChat.send")}
        </Button>
      </Box>
    </Dialog>
  );
};

// Chat-list preview text. The backend gives media as "[ptt]"/"[image]", and an uncaptioned photo's
// body is WhatsApp's base64 thumbnail — show a readable label for both instead.
const previewText = (body, t) => {
  const b = body || "";
  const tag = /^\[(\w+)\]$/.exec(b.trim());
  if (tag) return t(`whatsappChat.mediaLabels.${tag[1]}`, { defaultValue: b });
  if (b.length > 100 && !/\s/.test(b) && /^[A-Za-z0-9+/]+=*$/.test(b)) return t("whatsappChat.mediaLabels.image");
  return b;
};

// Same order the backend returns: pinned chats first (most recently pinned on top), then by
// last activity. Re-applied locally after a pin toggle or a live message.
const sortChats = (list) =>
  [...list].sort(
    (a, b) =>
      Number(!!b.pinned) - Number(!!a.pinned) ||
      (a.pinned ? (b.pinnedAt || 0) - (a.pinnedAt || 0) : 0) ||
      (b.timestamp || 0) - (a.timestamp || 0)
  );

const WA_GREEN = "#25d366"; // WhatsApp's unread green — same as the mobile app's T.wa

const recTime = (ms) => {
  const sec = Math.floor((ms || 0) / 1000);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
};

// Voice messages, WhatsApp Web style: click the mic to record; 🗑 cancels, Send sends.
// Browsers record Opus (ogg on Firefox, webm on Chrome); the backend sends it with
// sendAudioAsVoice so it arrives as a voice note.
const VOICE_MIME_CANDIDATES = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm"];
const VOICE_MIN_MS = 700;
const useVoiceRecorder = () => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null); // { recorder, stream, chunks, startedAt, mime }
  const timerRef = useRef(null);

  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    recRef.current?.stream.getTracks().forEach((track) => track.stop());
    recRef.current = null;
    setRecording(false);
    setElapsed(0);
  }, []);

  // Throws if the microphone isn't available or permission is denied.
  const start = useCallback(async () => {
    if (recRef.current) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = VOICE_MIME_CANDIDATES.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunks.push(e.data);
    };
    recorder.start(250);
    recRef.current = { recorder, stream, chunks, startedAt: Date.now(), mime: recorder.mimeType || mime || "audio/webm" };
    setRecording(true);
    timerRef.current = setInterval(() => {
      if (recRef.current) setElapsed(Date.now() - recRef.current.startedAt);
    }, 250);
  }, []);

  // Resolves to the recorded File, or null when cancelled / too short.
  const stop = useCallback(
    (keep) =>
      new Promise((resolve) => {
        const r = recRef.current;
        if (!r) {
          resolve(null);
          return;
        }
        const durationMs = Date.now() - r.startedAt;
        r.recorder.onstop = () => {
          const type = r.mime.split(";")[0];
          const blob = new Blob(r.chunks, { type });
          cleanup();
          if (!keep || durationMs < VOICE_MIN_MS || !blob.size) {
            resolve(null);
            return;
          }
          resolve(new File([blob], `voice_${Date.now()}.${type.includes("ogg") ? "ogg" : "webm"}`, { type }));
        };
        r.recorder.stop();
      }),
    [cleanup]
  );

  useEffect(
    () => () => {
      if (recRef.current) {
        try {
          recRef.current.recorder.stop();
        } catch (_) {
          /* already stopped */
        }
        cleanup();
      }
    },
    [cleanup]
  );

  return { recording, elapsed, start, stop };
};

const FORWARD_MAX_CHATS = 5; // same cap as WhatsApp (and the backend)

// "Forward to…" picker: search every chat, tick up to 5, send.
const ForwardDialog = ({ open, groupId, apiBase, count, sending, error, onClose, onSend, t }) => {
  const [chats, setChats] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState([]);
  const seqRef = useRef(0);

  const load = useCallback(
    async (q, offset = 0) => {
      const seq = ++seqRef.current;
      setLoading(true);
      const result = await getInboxChats(groupId, { limit: 30, offset, q, apiBase });
      if (seq !== seqRef.current) return;
      setLoading(false);
      if (result.success) {
        setChats((prev) => (offset === 0 ? result.data || [] : [...prev, ...(result.data || []).filter((c) => !prev.some((p) => p.id === c.id))]));
        setHasMore(!!result.hasMore);
      }
    },
    [groupId, apiBase]
  );

  useEffect(() => {
    if (!open) return;
    setPicked([]);
    setSearch("");
    load("");
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => load(search.trim()), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggle = (chat) =>
    setPicked((prev) =>
      prev.some((p) => p.id === chat.id)
        ? prev.filter((p) => p.id !== chat.id)
        : prev.length >= FORWARD_MAX_CHATS
          ? prev
          : [...prev, chat]
    );

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}` }}>
        <IconButton onClick={onClose} disabled={sending} aria-label={t("common.cancel")}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: TEXT_DARK }}>{t("whatsappChat.forwardTo")}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_SUBTLE }}>
            {t("whatsappChat.forwardCount", { count })} · {t("whatsappChat.forwardMax", { max: FORWARD_MAX_CHATS })}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 2, pt: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          color="secondary"
          autoFocus
          placeholder={t("whatsappChat.searchChats")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: TEXT_SUBTLE }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box
        sx={{ height: 380, overflowY: "auto", mt: 1 }}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 120) load(search.trim(), chats.length);
        }}
      >
        {chats.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            {loading ? (
              <CircularProgress size={24} color="secondary" />
            ) : (
              <Typography sx={{ fontFamily: FONT, color: TEXT_SUBTLE, fontSize: 13 }}>{t("whatsappChat.noChats")}</Typography>
            )}
          </Box>
        ) : (
          <List disablePadding>
            {chats.map((chat) => {
              const on = picked.some((p) => p.id === chat.id);
              return (
                <ListItemButton
                  key={chat.id}
                  onClick={() => toggle(chat)}
                  disabled={sending || (!on && picked.length >= FORWARD_MAX_CHATS)}
                >
                  <ListItemAvatar>
                    <ChatAvatar groupId={groupId} apiBase={apiBase} chat={chat} size={36} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 14, color: TEXT_DARK }}>
                        {chat.name}
                      </Typography>
                    }
                  />
                  <Checkbox edge="end" color="secondary" checked={on} tabIndex={-1} disableRipple />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, borderTop: `1px solid ${BORDER}`, bgcolor: BG_TINT }}>
        <Typography noWrap sx={{ flex: 1, fontFamily: FONT, fontSize: 13, color: TEXT_SECONDARY }}>
          {picked.map((p) => p.name).join(", ")}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          disabled={sending || picked.length === 0}
          onClick={() => onSend(picked.map((p) => p.id))}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          {t("whatsappChat.send")}
        </Button>
      </Box>
    </Dialog>
  );
};

// Two entry points render this component:
//   1. WhatsApp settings > instance > "Open Chat" — route /whatsapp-chat/:groupId, admin-only.
//      Rendered with no props; reads groupId from the route and defaults apiBase="settings".
//   2. The "WhatsApp Chat" menu (scenes/whatsappChat/index.jsx) — its own instance picker
//      passes `groupId` + `apiBase="mychat"` + `backTo={null}` + `headerExtra` (instance
//      switcher) directly as props, no route param involved.
const WhatsAppChat = ({ groupId: groupIdProp, apiBase = "settings", backTo = "/whatsappgroup", headerExtra = null } = {}) => {
  const { groupId: groupIdFromRoute } = useParams();
  const groupId = groupIdProp || groupIdFromRoute;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState("loading"); // loading | ready | not_ready
  // Which WhatsApp instance this page is showing (name + connected number), for the header.
  const [instanceInfo, setInstanceInfo] = useState(null);

  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [chatSearch, setChatSearch] = useState("");
  const [chatFilter, setChatFilter] = useState("all");
  const [chatsHasMore, setChatsHasMore] = useState(false);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);

  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageSearch, setMessageSearch] = useState("");
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false); // media / voice sends (text sends are optimistic)
  // Text messages shown in the thread right away, like WhatsApp Web, while they're sent in the
  // background: { tempId, chatId, body, quoted, timestamp, status: "sending" | "sent" | "failed", key }.
  // Dropped once the real message (same key) shows up in `messages` via the socket / a reload.
  const [outgoing, setOutgoing] = useState([]);
  const sendQueueRef = useRef(Promise.resolve()); // sends go out one at a time, in typed order
  const [alert, setAlert] = useState({ show: false, message: "", severity: "info" });
  const [viewerImage, setViewerImage] = useState(null); // { slides, index } for ImageViewer
  const [replyTo, setReplyTo] = useState(null); // quoteOf(message) the next send replies to
  const [editing, setEditing] = useState(null); // { id, chatId, body } — own message being edited in the composer
  const [highlightId, setHighlightId] = useState(null); // message flashed after jumping to it from a quote
  const [pendingFile, setPendingFile] = useState(null); // picked attachment awaiting "Send" in the preview
  const [pendingFileError, setPendingFileError] = useState("");
  const [composerFiles, setComposerFiles] = useState(null); // picked photos → MediaComposer (caption / edit / send)
  // Message menu (hover ⌄ / right-click): { m, anchorEl } or { m, anchorPosition }.
  const [msgMenu, setMsgMenu] = useState(null);
  // Chat-row menu (hover ⌄ / right-click): { chat, anchorEl } or { chat, anchorPosition }.
  const [chatMenu, setChatMenu] = useState(null);
  // Selection mode for Forward: null = off, otherwise a Set of selected message ids.
  const [selectedIds, setSelectedIds] = useState(null);
  const selecting = selectedIds !== null;
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [forwardError, setForwardError] = useState("");
  const voice = useVoiceRecorder();
  const [infoOpen, setInfoOpen] = useState(false);
  const [mediaPanel, setMediaPanel] = useState(null); // { fromInfo } — "Media, links and docs" panel
  const [headerMenu, setHeaderMenu] = useState(null); // ⋮ anchor
  const [searchOpen, setSearchOpen] = useState(false); // in-chat search bar
  const [matchIdx, setMatchIdx] = useState(0);
  const [infoMember, setInfoMember] = useState(null); // a group sender clicked in the thread → open straight on their profile
  // Quick replies ("/" in the composer): global + this instance's, with `context` (customer
  // name / phone / language) for the open chat.
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrContext, setQrContext] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrLang, setQrLang] = useState("en");
  const [qrIndex, setQrIndex] = useState(0);
  const [qrDismissed, setQrDismissed] = useState(false); // Esc / close until the "/" is retyped
  const [qrEdit, setQrEdit] = useState(null); // { reply } — reply null = new
  const [qrSend, setQrSend] = useState(null); // { reply, text } — reply with photos/videos awaiting Send

  const socketRef = useRef(null);
  const selectedChatRef = useRef(null);
  const chatsRef = useRef([]);
  const loadChatsRef = useRef(null);
  const chatsReloadTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const messagesBoxRef = useRef(null);
  // Scroll bookkeeping for the thread: snap to bottom on open/new message; when older
  // messages are prepended, keep the view anchored on what the user was reading.
  const stickToBottomRef = useRef(true);
  const prependAnchorRef = useRef(null);
  // Current server-side chat search, and a sequence number so a slow response for an old
  // query/page can't overwrite a newer one.
  const chatQueryRef = useRef("");
  const chatFilterRef = useRef("all");
  const chatsLoadSeqRef = useRef(0);

  const showAlert = useCallback((message, severity = "info") => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: "", severity: "info" }), 5000);
  }, []);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // First page of the chat list for `query` (defaults to the current search). The search runs
  // on the server across every chat WhatsApp Web knows about, not just the loaded page.
  // `silent` = background refresh (live message for a chat not in the list): no spinner/alert.
  const loadChats = useCallback(
    async (query, { silent = false } = {}) => {
      const q = query === undefined ? chatQueryRef.current : query;
      chatQueryRef.current = q;
      const seq = ++chatsLoadSeqRef.current;
      if (!silent) setLoadingChats(true);
      const result = await getInboxChats(groupId, {
        limit: CHAT_PAGE_SIZE,
        offset: 0,
        q,
        filter: chatFilterRef.current,
        apiBase,
      });
      if (seq !== chatsLoadSeqRef.current) return;
      setLoadingChats(false);
      if (result.success) {
        setChats(result.data || []);
        setChatsHasMore(!!result.hasMore);
      } else if (!silent) {
        showAlert(result.message || t("whatsappChat.loadFailed"), "error");
      }
    },
    [groupId, apiBase, showAlert, t]
  );
  loadChatsRef.current = loadChats;

  const loadMoreChats = useCallback(async () => {
    if (loadingChats || loadingMoreChats || !chatsHasMore) return;
    const seq = chatsLoadSeqRef.current;
    setLoadingMoreChats(true);
    const result = await getInboxChats(groupId, {
      limit: CHAT_PAGE_SIZE,
      offset: chats.length,
      q: chatQueryRef.current,
      filter: chatFilterRef.current,
      apiBase,
    });
    setLoadingMoreChats(false);
    if (seq !== chatsLoadSeqRef.current) return; // search changed meanwhile
    if (result.success) {
      setChats((prev) => {
        const ids = new Set(prev.map((c) => c.id));
        return [...prev, ...(result.data || []).filter((c) => !ids.has(c.id))];
      });
      setChatsHasMore(!!result.hasMore);
    }
  }, [groupId, apiBase, chats.length, chatsHasMore, loadingChats, loadingMoreChats]);

  // WhatsApp Web-style list filter (All / Unread / Favorites / Groups) — server-side, like the
  // search, and combined with it.
  const changeChatFilter = useCallback(
    (filter) => {
      if (filter === chatFilterRef.current) return;
      chatFilterRef.current = filter;
      setChatFilter(filter);
      loadChats();
    },
    [loadChats]
  );

  // Debounced server-side chat search.
  useEffect(() => {
    const q = chatSearch.trim();
    if (status !== "ready" || q === chatQueryRef.current) return undefined;
    const timer = setTimeout(() => loadChats(q), 350);
    return () => clearTimeout(timer);
  }, [chatSearch, status, loadChats]);

  // Not logged in → just say so. Pairing (QR) is done by an admin in WhatsApp Settings; this
  // page never starts a client or shows a QR. It flips to the chat by itself once the
  // instance becomes ready (socket events below).
  const checkStatus = useCallback(async () => {
    const result = await getGroupStatus(groupId, apiBase);
    if (result.success && result.data) {
      setInstanceInfo({
        name: result.data.name || "",
        phone: result.data.whatsappPhoneNumber || "",
      });
    }
    if (result.success && result.data?.whatsappStatus === "ready") {
      setStatus("ready");
      loadChats();
    } else {
      setStatus("not_ready");
    }
  }, [groupId, apiBase, loadChats]);

  useEffect(() => {
    setStatus("loading");
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, apiBase]);

  // Own socket.io-client connection, following the same per-screen convention used by
  // WhatsAppInstance.jsx / WhatsAppQueue.jsx (no shared socket hook in this codebase).
  useEffect(() => {
    const socketUrl =
      process.env.NODE_ENV === "production"
        ? window.location.origin
        : process.env.REACT_APP_SOCKET_IO || "http://localhost:5000";
    const socket = io(socketUrl, {
      rejectUnauthorized: false,
      secure: process.env.NODE_ENV === "production",
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on(`whatsapp-authenticated-${groupId}`, () => {
      setStatus("ready");
      loadChats();
    });

    socket.on(`whatsapp-status-${groupId}`, (data) => {
      if (data?.status === "ready") {
        setStatus("ready");
        loadChats();
      } else if (["disconnected", "auth_failure", "init_error"].includes(data?.status)) {
        setStatus("not_ready");
      }
    });

    // A message was edited / deleted for everyone (here, on the phone, or by the other side).
    socket.on(`whatsapp-chat-message-update-${groupId}`, (data) => {
      if (!data?.id) return;
      setMessages((prev) =>
        prev.map((m) =>
          sameMessage(m.id, data.id)
            ? data.revoked
              ? { ...m, revoked: true, body: "", hasMedia: false, linkPreview: null, quoted: null }
              : { ...m, body: data.body ?? m.body, edited: true }
            : m
        )
      );
    });

    socket.on(`whatsapp-chat-message-${groupId}`, (data) => {
      // Chat not in the loaded list — a brand-new contact, or (most often) a sent message
      // echoed under the phone id "…@c.us" while the list holds the chat as "…@lid".
      // Quietly reload the first page (debounced: a bulk send fires one event per message).
      if (!chatsRef.current.some((c) => c.id === data.chatId)) {
        clearTimeout(chatsReloadTimerRef.current);
        chatsReloadTimerRef.current = setTimeout(() => loadChatsRef.current?.(undefined, { silent: true }), 1000);
      }
      // Update (or move to top of) the chat list entry.
      setChats((prev) => {
        const idx = prev.findIndex((c) => c.id === data.chatId);
        const isOpen = selectedChatRef.current?.id === data.chatId;
        const preview = { body: data.hasMedia ? `[${data.mediaType || "media"}]` : data.body, timestamp: data.timestamp, fromMe: data.fromMe };
        if (idx === -1) return prev; // reloaded above
        const updated = { ...prev[idx], lastMessage: preview, timestamp: data.timestamp };
        if (!data.fromMe && !isOpen) {
          updated.unreadCount = Math.max(prev[idx].unreadCount || 0, 0) + 1; // -1 = "marked unread"
        }
        const next = [...prev];
        next[idx] = updated;
        return sortChats(next); // to the top — below any pinned chats
      });

      // Append to the open thread if it belongs to the currently open chat. Follow it to the
      // bottom only if the user was already there (or sent it) — don't yank them away from
      // older history they scrolled up to read.
      if (selectedChatRef.current?.id === data.chatId) {
        const box = messagesBoxRef.current;
        stickToBottomRef.current =
          data.fromMe || !box || box.scrollHeight - box.scrollTop - box.clientHeight < 150;
        setMessages((prev) => (data.id && prev.some((m) => sameMessage(m.id, data.id)) ? prev : [...prev, data]));
      }
    });

    return () => {
      clearTimeout(chatsReloadTimerRef.current);
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useLayoutEffect(() => {
    const box = messagesBoxRef.current;
    if (!box) return;
    if (prependAnchorRef.current) {
      // Older messages were added above: keep the same content under the user's eyes.
      const anchor = prependAnchorRef.current;
      prependAnchorRef.current = null;
      box.scrollTop = box.scrollHeight - anchor.scrollHeight + anchor.scrollTop;
      return;
    }
    if (stickToBottomRef.current) {
      box.scrollTop = box.scrollHeight;
    }
  }, [messages, outgoing]);

  const openChat = useCallback(
    async (chat) => {
      setSelectedChat(chat);
      selectedChatRef.current = chat;
      setMessages([]);
      setMessageSearch("");
      setMessagesHasMore(false);
      setLoadingMessages(true);
      stickToBottomRef.current = true;
      prependAnchorRef.current = null;

      // Clear the unread badge locally right away — sendSeen() below tells WhatsApp itself.
      setChats((prev) => prev.map((c) => (c.id === chat.id ? { ...c, unreadCount: 0 } : c)));

      const result = await getInboxChatMessages(groupId, chat.id, { apiBase });
      if (selectedChatRef.current?.id !== chat.id) return; // user switched chats meanwhile
      setLoadingMessages(false);
      if (result.success) {
        stickToBottomRef.current = true;
        setMessages(result.data || []);
        setMessagesHasMore(!!result.hasMore);
      } else {
        showAlert(result.message || t("whatsappChat.loadFailed"), "error");
      }

      markInboxChatRead(groupId, chat.id, apiBase).catch(() => {});
      refreshProfilePic(groupId, chat.id, apiBase);
    },
    [groupId, apiBase, showAlert, t]
  );

  // Infinite scroll upward: fetch the page of messages just before the oldest loaded one.
  const loadOlderMessages = useCallback(async () => {
    const chat = selectedChatRef.current;
    if (!chat || loadingMessages || loadingOlder || !messagesHasMore || messages.length === 0) return;
    const oldestId = messages[0].id;
    if (!oldestId) return;

    setLoadingOlder(true);
    const result = await getInboxChatMessages(groupId, chat.id, { before: oldestId, apiBase });
    setLoadingOlder(false);
    if (selectedChatRef.current?.id !== chat.id) return;

    if (result.success) {
      const box = messagesBoxRef.current;
      if (box) prependAnchorRef.current = { scrollHeight: box.scrollHeight, scrollTop: box.scrollTop };
      stickToBottomRef.current = false;
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.id));
        return [...(result.data || []).filter((m) => !ids.has(m.id)), ...prev];
      });
      setMessagesHasMore(!!result.hasMore);
    } else {
      showAlert(result.message || t("whatsappChat.loadFailed"), "error");
    }
  }, [groupId, apiBase, loadingMessages, loadingOlder, messagesHasMore, messages, showAlert, t]);

  const dispatchOutgoing = useCallback(
    (item) => {
      const patch = (changes) => setOutgoing((prev) => prev.map((o) => (o.tempId === item.tempId ? { ...o, ...changes } : o)));
      sendQueueRef.current = sendQueueRef.current.then(async () => {
        const result = await sendInboxChatMessage(groupId, item.chatId, item.body, apiBase, { quotedMessageId: item.quoted?.id });
        if (result.success && !result.data?.messageId) {
          // No id to match the socket echo by → drop the placeholder (the echo shows the message).
          setOutgoing((prev) => prev.filter((o) => o.tempId !== item.tempId));
        } else if (result.success) {
          patch({ status: "sent", key: result.data.messageId });
        } else {
          patch({ status: "failed" });
          const message = isNoLidError(result.message) ? t("whatsappChat.noLidError") : result.message || t("whatsappChat.sendFailed");
          showAlert(message, "error");
        }
      });
    },
    [groupId, apiBase, showAlert, t]
  );

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setMessageText("");
  }, []);

  // Save an edit: update the bubble right away, roll back if WhatsApp refuses.
  const saveEdit = useCallback(async () => {
    const text = messageText.trim();
    const target = editing;
    if (!target || !text) return;
    setEditing(null);
    setMessageText("");
    if (text === target.body) return; // nothing changed
    const previous = messages.find((m) => m.id === target.id);
    const patchMsg = (changes) => setMessages((prev) => prev.map((m) => (m.id === target.id ? { ...m, ...changes } : m)));
    patchMsg({ body: text, edited: true });
    const result = await editInboxChatMessage(groupId, target.chatId, target.id, text, apiBase);
    if (result.success) {
      if (result.data?.body) patchMsg({ body: result.data.body }); // with the "sent by" footer
    } else {
      if (previous) patchMsg({ body: previous.body, edited: previous.edited });
      showAlert(result.code === "EDIT_EXPIRED" ? t("whatsappChat.editExpired") : result.message || t("whatsappChat.editFailed"), "error");
    }
  }, [messageText, editing, messages, groupId, apiBase, showAlert, t]);

  const handleStartEdit = useCallback(() => {
    const m = msgMenu?.m;
    setMsgMenu(null);
    if (!canEditMsg(m)) return;
    const body = String(m.body || "").replace(FOOTER_RE, "").trimEnd();
    setReplyTo(null);
    setEditing({ id: m.id, chatId: selectedChat.id, body });
    setMessageText(body);
    setTimeout(() => messageInputRef.current?.focus(), 0);
  }, [msgMenu, selectedChat]);

  const handleRevoke = useCallback(async () => {
    const m = msgMenu?.m;
    setMsgMenu(null);
    if (!canRevokeMsg(m) || !selectedChat) return;
    const chatId = selectedChat.id;
    const confirm = await Swal.fire({
      title: t("whatsappChat.deleteForEveryoneConfirm"),
      text: t("whatsappChat.deleteForEveryoneHint"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("whatsappChat.deleteForEveryone"),
      cancelButtonText: t("common.cancel"),
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;
    const result = await revokeInboxChatMessage(groupId, chatId, m.id, apiBase);
    if (result.success) {
      setMessages((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, revoked: true, body: "", hasMedia: false, linkPreview: null, quoted: null } : x))
      );
      if (editing?.id === m.id) cancelEdit();
    } else {
      showAlert(
        result.code === "REVOKE_EXPIRED" ? t("whatsappChat.deleteExpired") : result.message || t("whatsappChat.deleteFailed"),
        "error"
      );
    }
  }, [msgMenu, selectedChat, groupId, apiBase, editing, cancelEdit, showAlert, t]);

  // Leaving the chat (or Esc) drops an unfinished edit — never send it into another chat.
  useEffect(() => {
    if (editing && editing.chatId !== selectedChat?.id) cancelEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.id]);
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !viewerImage && !pendingFile && !composerFiles) cancelEdit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, viewerImage, pendingFile, composerFiles, cancelEdit]);

  const loadQuickReplies = useCallback(
    async (chat) => {
      setQrLoading(true);
      const result = await getQuickReplies(groupId, { chatId: chat?.id || null, apiBase });
      if (selectedChatRef.current?.id !== chat?.id) return; // switched chats meanwhile
      setQrLoading(false);
      if (!result.success) return;
      setQuickReplies(result.data);
      setQrContext(result.context);
      setQrLang(toQrLang(result.context?.language || i18n.language));
    },
    [groupId, apiBase, i18n.language]
  );

  useEffect(() => {
    if (!selectedChat?.id) return;
    setQrContext(null);
    loadQuickReplies(selectedChat);
    // Only on chat switch — not on every selectedChat object refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.id, loadQuickReplies]);

  const qrMatch = /^\/(\S*)$/.exec(messageText);
  const qrOpen = !!qrMatch && !qrDismissed && !editing && !!selectedChat;
  const qrQuery = qrMatch ? qrMatch[1] : "";
  const qrMatches = useMemo(() => filterQuickReplies(quickReplies, qrQuery.toLowerCase()), [quickReplies, qrQuery]);

  useEffect(() => {
    setQrIndex(0);
    if (!messageText.startsWith("/")) setQrDismissed(false);
  }, [qrQuery, messageText]);

  // Chat users manage only their instance's replies; the admin settings inbox can edit all.
  const canEditQuickReply = useCallback((r) => apiBase === "settings" || r.groupId === groupId, [apiBase, groupId]);

  const pickQuickReply = useCallback(
    (reply) => {
      const text = fillQuickReply(quickReplyText(reply, qrLang), qrContext);
      if (reply.attachments.length) {
        setMessageText("");
        setQrSend({ reply, text });
        return;
      }
      setMessageText(text);
      requestAnimationFrame(() => {
        const el = messageInputRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(text.length, text.length);
        }
      });
    },
    [qrLang, qrContext]
  );

  const handleSendQuickReply = useCallback(
    async (text) => {
      const { reply } = qrSend || {};
      if (!reply || !selectedChat) return { success: false };
      const result = await sendQuickReply(groupId, selectedChat.id, reply._id, text, apiBase, {
        quotedMessageId: replyTo?.id,
        count: reply.attachments.length,
      });
      if (result.success) {
        setQrSend(null);
        setReplyTo(null);
        stickToBottomRef.current = true;
        return result;
      }
      const message = isNoLidError(result.message) ? t("whatsappChat.noLidError") : result.message;
      if (result.sent > 0) {
        setReplyTo(null); // the reply already went out with the first item
        return { success: false, message: t("quickReplies.sentPartial", { sent: result.sent, total: reply.attachments.length, message }) };
      }
      return { success: false, message };
    },
    [qrSend, selectedChat, groupId, apiBase, replyTo, t]
  );

  const handleSaveQuickReply = useCallback(
    async (data, files) => {
      const result = await saveQuickReply(groupId, qrEdit?.reply?._id || null, data, files, apiBase);
      if (result.success) {
        setQrEdit(null);
        loadQuickReplies(selectedChatRef.current);
      }
      return result;
    },
    [groupId, apiBase, qrEdit, loadQuickReplies]
  );

  const handleDeleteQuickReply = useCallback(
    async (reply) => {
      const result = await deleteQuickReply(groupId, reply._id, apiBase);
      if (result.success) {
        setQrEdit(null);
        loadQuickReplies(selectedChatRef.current);
      }
      return result;
    },
    [groupId, apiBase, loadQuickReplies]
  );

  const handleSend = useCallback(() => {
    if (editing) {
      saveEdit();
      return;
    }
    const text = messageText.trim();
    if (!text || !selectedChat) return;
    const item = {
      tempId: `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      chatId: selectedChat.id,
      body: text,
      quoted: replyTo,
      timestamp: moment().unix(),
      status: "sending",
      key: null,
    };
    setMessageText("");
    setReplyTo(null);
    stickToBottomRef.current = true;
    setOutgoing((prev) => [...prev, item]);
    dispatchOutgoing(item);
  }, [editing, saveEdit, messageText, selectedChat, replyTo, dispatchOutgoing]);

  const retryOutgoing = useCallback(
    (item) => {
      setOutgoing((prev) => prev.map((o) => (o.tempId === item.tempId ? { ...o, status: "sending" } : o)));
      dispatchOutgoing(item);
    },
    [dispatchOutgoing]
  );

  const discardOutgoing = useCallback((item) => {
    setOutgoing((prev) => prev.filter((o) => o.tempId !== item.tempId));
  }, []);

  // The real message arrived (socket echo or reload) → drop its placeholder.
  useEffect(() => {
    setOutgoing((prev) => {
      const sent = prev.filter((o) => o.key);
      if (!sent.length) return prev;
      const keys = new Set(messages.map((m) => msgKey(m.id)));
      const next = prev.filter((o) => !o.key || !keys.has(o.key));
      return next.length === prev.length ? prev : next;
    });
  }, [messages, outgoing]);

  // Photos (one or many) open the WhatsApp-style composer (caption each, edit, send together);
  // a single document keeps the plain preview. Documents go one at a time.
  const openPicked = useCallback(
    (files) => {
      if (!files.length || !selectedChat) return;
      const photos = files.filter((f) => f.type.startsWith("image/"));
      if (photos.length === files.length) {
        setComposerFiles(photos);
        return;
      }
      if (files.length === 1) {
        setPendingFileError("");
        setPendingFile(files[0]); // preview first; sent from the preview dialog
        return;
      }
      showAlert(t("whatsappChat.docsOneAtATime"), "info");
      if (photos.length) setComposerFiles(photos);
    },
    [selectedChat, showAlert, t]
  );

  const handleAttach = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      e.target.value = ""; // allow picking the same file again later
      openPicked(files);
    },
    [openPicked]
  );

  // Pasting an image (e.g. a screenshot) into the message box opens the photo composer.
  const handlePaste = useCallback(
    (e) => {
      const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
      if (!files.length) return;
      e.preventDefault();
      openPicked(files);
    },
    [openPicked]
  );

  const handleSendPhotos = useCallback(
    async (files, captions) => {
      if (!selectedChat) return { success: false };
      const result = await sendInboxChatMediaBatch(groupId, selectedChat.id, files, captions, apiBase, { quotedMessageId: replyTo?.id });
      if (result.success) {
        setComposerFiles(null);
        setReplyTo(null);
        return result;
      }
      if (result.sent > 0) setReplyTo(null); // the reply already went out with the first photo
      return { ...result, message: isNoLidError(result.message) ? t("whatsappChat.noLidError") : result.message };
    },
    [groupId, apiBase, selectedChat, replyTo, t]
  );

  const handleSendAttachment = useCallback(async () => {
    if (!pendingFile || !selectedChat || sending) return;
    setSending(true);
    setPendingFileError("");
    const result = await sendInboxChatMedia(groupId, selectedChat.id, pendingFile, apiBase, { quotedMessageId: replyTo?.id });
    setSending(false);
    if (result.success) {
      setPendingFile(null);
      setReplyTo(null);
    } else {
      // Keep the preview open so the user can retry or cancel.
      setPendingFileError(isNoLidError(result.message) ? t("whatsappChat.noLidError") : result.message || t("whatsappChat.sendFailed"));
    }
  }, [groupId, apiBase, pendingFile, selectedChat, sending, replyTo, t]);

  // Leaving a chat ends selection mode and throws away an unsent recording.
  useEffect(() => {
    setSelectedIds(null);
    setMsgMenu(null);
    setReplyTo(null);
    setInfoMember(null);
    setSearchOpen(false);
    setMediaPanel(null);
    voice.stop(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.id]);

  // Esc leaves selection mode.
  useEffect(() => {
    if (!selecting) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !forwardOpen) setSelectedIds(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selecting, forwardOpen]);

  const toggleSelect = useCallback((m) => {
    setSelectedIds((prev) => {
      const next = new Set(prev || []);
      if (next.has(m.id)) next.delete(m.id);
      else next.add(m.id);
      return next.size ? next : null; // unselecting the last one leaves selection mode
    });
  }, []);

  const handleCopy = useCallback(async () => {
    const m = msgMenu?.m;
    setMsgMenu(null);
    if (!m?.body) return;
    try {
      await navigator.clipboard.writeText(m.body);
      showAlert(t("whatsappChat.copied"), "success");
    } catch (_) {
      showAlert(t("whatsappChat.copyFailed"), "error");
    }
  }, [msgMenu, showAlert, t]);

  // Copy a photo to the clipboard. Browsers only accept PNG there, so the image is redrawn
  // through a canvas; the blob is handed over as a promise so the clipboard call itself still
  // happens inside the click (Safari requires that).
  const handleCopyImage = useCallback(async () => {
    const m = msgMenu?.m;
    setMsgMenu(null);
    if (!m?.id || !selectedChat) return;
    try {
      const png = (async () => {
        const res = await fetch(getInboxChatMediaUrl(groupId, selectedChat.id, m.id, apiBase), { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const bitmap = await createImageBitmap(await res.blob());
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext("2d").drawImage(bitmap, 0, 0);
        return new Promise((resolve, reject) =>
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png")
        );
      })();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": png })]);
      showAlert(t("whatsappChat.imageCopied"), "success");
    } catch (_) {
      showAlert(t("whatsappChat.imageCopyFailed"), "error");
    }
  }, [msgMenu, selectedChat, groupId, apiBase, showAlert, t]);

  // Save a photo / video to the computer (browser download).
  const handleSaveMedia = useCallback(() => {
    const m = msgMenu?.m;
    setMsgMenu(null);
    if (!m?.id || !selectedChat) return;
    const ext = m.mediaType === "video" ? "mp4" : /png/i.test(m.mimetype || "") ? "png" : "jpg";
    const a = document.createElement("a");
    a.href = getInboxChatMediaUrl(groupId, selectedChat.id, m.id, apiBase);
    a.download = m.filename || `whatsapp_${m.timestamp}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [msgMenu, selectedChat, groupId, apiBase]);

  // "Mark as unread" (WhatsApp's green dot = unreadCount -1) / "Mark as read".
  const handleToggleUnread = useCallback(async () => {
    const chat = chatMenu?.chat;
    setChatMenu(null);
    if (!chat) return;
    const makeUnread = !chat.unreadCount;
    const result = makeUnread
      ? await markInboxChatUnread(groupId, chat.id, apiBase)
      : await markInboxChatRead(groupId, chat.id, apiBase);
    if (!result.success) {
      showAlert(result.message || t("whatsappChat.actionFailed"), "error");
      return;
    }
    setChats((prev) => prev.map((c) => (c.id === chat.id ? { ...c, unreadCount: makeUnread ? -1 : 0 } : c)));
  }, [chatMenu, groupId, apiBase, showAlert, t]);

  const handleTogglePin = useCallback(async () => {
    const chat = chatMenu?.chat;
    setChatMenu(null);
    if (!chat) return;
    const result = await setInboxChatPinned(groupId, chat.id, !chat.pinned, apiBase);
    if (!result.success) {
      showAlert(result.message || t("whatsappChat.actionFailed"), "error");
      return;
    }
    setChats((prev) =>
      sortChats(prev.map((c) => (c.id === chat.id ? { ...c, pinned: result.pinned, pinnedAt: result.pinned ? Math.floor(Date.now() / 1000) : 0 } : c)))
    );
  }, [chatMenu, groupId, apiBase, showAlert, t]);

  const handleReply = useCallback(() => {
    const m = msgMenu?.m;
    setMsgMenu(null);
    if (!m?.id) return;
    if (editing) cancelEdit();
    setReplyTo(quoteOf(m));
    setTimeout(() => messageInputRef.current?.focus(), 0);
  }, [msgMenu, editing, cancelEdit]);

  // Esc cancels the reply (when nothing else is open on top).
  useEffect(() => {
    if (!replyTo) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !viewerImage && !pendingFile && !composerFiles) setReplyTo(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [replyTo, viewerImage, pendingFile, composerFiles]);

  // Click a quote → scroll to the original message and flash it. Only loaded messages can be
  // reached; older ones need "load older" first.
  const jumpToMessage = useCallback(
    (id) => {
      const target = messages.find((m) => sameMessage(m.id, id));
      const el = target && messagesBoxRef.current?.querySelector(`[data-msg-id="${CSS.escape(target.id)}"]`);
      if (!el) {
        showAlert(t("whatsappChat.quotedNotLoaded"), "info");
        return;
      }
      stickToBottomRef.current = false;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(target.id);
      setTimeout(() => setHighlightId((cur) => (cur === target.id ? null : cur)), 1600);
    },
    [messages, showAlert, t]
  );

  // A photo from the thread opens the viewer on every photo of the loaded chat (swipe between
  // them); anything else (e.g. a profile photo) opens on its own.
  const openImage = useCallback(
    (img) => {
      if (!img) return;
      if (img.messageId && selectedChat) {
        const photos = messages.filter((x) => x.id && x.hasMedia && x.mediaType === "image");
        const index = photos.findIndex((x) => x.id === img.messageId);
        if (index !== -1) {
          setViewerImage({
            index,
            slides: photos.map((x) => ({
              src: getInboxChatMediaUrl(groupId, selectedChat.id, x.id, apiBase),
              fallback: x.thumbnail ? `data:image/jpeg;base64,${x.thumbnail}` : null,
              filename: x.filename,
            })),
          });
          return;
        }
      }
      setViewerImage({ index: 0, slides: [img] });
    },
    [messages, selectedChat, groupId, apiBase]
  );

  // Group sender name / member → their profile in the info panel.
  const openSenderProfile = useCallback((m) => {
    if (!m?.author) return;
    setInfoMember({ id: m.author, name: m.authorName || m.authorPushname || "", number: m.authorNumber || null });
    setInfoOpen(true);
  }, []);

  // "Message" on a member's profile: their existing chat if it's in the list, otherwise a new
  // one on the member's own WhatsApp id (usually @lid — a "<number>@c.us" id for someone this
  // number never messaged hits WhatsApp's "No LID for user", see isNoLidError).
  const messageMember = useCallback(
    (mem, memberInfo) => {
      const number = memberInfo?.number || mem.number;
      const ids = [mem.id, number ? `${number}@c.us` : null, memberInfo?.lid ? `${memberInfo.lid}@lid` : null].filter(Boolean);
      const existing = chats.find((c) => ids.includes(c.id));
      const target = existing || {
        id: mem.id,
        name: memberInfo?.name || mem.name || memberInfo?.pushname || (number ? `+${number}` : mem.id.split("@")[0]),
        isGroup: false,
        unreadCount: 0,
      };
      setInfoOpen(false);
      setInfoMember(null);
      openChat(target);
    },
    [chats, openChat]
  );

  const handleStartForward = useCallback(() => {
    const m = msgMenu?.m;
    setMsgMenu(null);
    if (m?.id) setSelectedIds(new Set([m.id]));
  }, [msgMenu]);

  const handleForward = useCallback(
    async (toChatIds) => {
      if (!selectedIds || forwarding) return;
      const ids = messages.filter((m) => selectedIds.has(m.id)).map((m) => m.id); // chronological
      setForwarding(true);
      setForwardError("");
      const result = await forwardInboxMessages(groupId, ids, toChatIds, apiBase);
      setForwarding(false);
      if (!result.success) {
        setForwardError(result.message || t("whatsappChat.forwardFailed"));
        return;
      }
      setForwardOpen(false);
      setSelectedIds(null);
      if (result.message) showAlert(result.message, "warning"); // some targets failed
      else showAlert(t("whatsappChat.forwarded", { count: result.okCount }), "success");
    },
    [groupId, apiBase, messages, selectedIds, forwarding, showAlert, t]
  );

  const handleMic = useCallback(async () => {
    try {
      await voice.start();
    } catch (_) {
      showAlert(t("whatsappChat.micPermission"), "error");
    }
  }, [voice, showAlert, t]);

  const handleSendVoice = useCallback(async () => {
    if (!selectedChat) return;
    const file = await voice.stop(true);
    if (!file) {
      showAlert(t("whatsappChat.voiceTooShort"), "info");
      return;
    }
    setSending(true);
    const result = await sendInboxChatMedia(groupId, selectedChat.id, file, apiBase, { asVoice: true, quotedMessageId: replyTo?.id });
    setSending(false);
    if (result.success) {
      setReplyTo(null);
    } else {
      const message = isNoLidError(result.message) ? t("whatsappChat.noLidError") : result.message || t("whatsappChat.sendFailed");
      showAlert(message, "error");
    }
  }, [groupId, apiBase, selectedChat, voice, replyTo, showAlert, t]);

  // Chat search is done server-side (see loadChats), so the list is already filtered.
  const filteredChats = chats;

  // In-chat search (like WhatsApp): matches among the loaded messages, newest first; ▲ / ▼ step
  // through them and the current one is scrolled to and highlighted (the thread isn't filtered).
  const searchMatches = useMemo(() => {
    const q = messageSearch.trim().toLowerCase();
    if (!searchOpen || !q) return [];
    return messages.filter((m) => m.id && (m.body || "").toLowerCase().includes(q)).map((m) => m.id).reverse();
  }, [messages, messageSearch, searchOpen]);
  const currentMatch = searchMatches[Math.min(matchIdx, searchMatches.length - 1)] || null;
  useEffect(() => {
    setMatchIdx(0);
  }, [messageSearch]);
  useEffect(() => {
    if (!searchOpen) return;
    setHighlightId(currentMatch);
    if (!currentMatch) return;
    stickToBottomRef.current = false;
    messagesBoxRef.current
      ?.querySelector(`[data-msg-id="${CSS.escape(currentMatch)}"]`)
      // Instant: a smooth scroll that passes the top triggers "load older", whose re-anchoring
      // cuts the animation short and leaves the match off screen.
      ?.scrollIntoView({ block: "center" });
  }, [currentMatch, searchOpen]);
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setMessageSearch("");
    setHighlightId(null);
  }, []);

  const filteredMessages = messages; // search no longer filters the thread (see searchMatches)

  const instanceSubtitle = instanceInfo
    ? `${t("whatsappChat.instance")}: ${instanceInfo.name}${instanceInfo.phone ? ` · +${instanceInfo.phone}` : ""}`
    : undefined;

  const headerActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {headerExtra}
      {backTo && (
        <IconButton color="secondary" onClick={() => navigate(backTo)} aria-label={t("whatsappChat.back")}>
          <ArrowBackIcon />
        </IconButton>
      )}
    </Box>
  );

  if (status !== "ready") {
    return (
      <PageShell title={t("whatsappChat.title")} subtitle={instanceSubtitle} actions={headerActions}>
        {alert.show && (
          <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
            {alert.message}
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 8,
            border: `1px solid ${BORDER}`,
            borderRadius: RADIUS_CARD,
            bgcolor: BG_TINT,
          }}
        >
          {status === "loading" ? (
            <CircularProgress color="secondary" />
          ) : (
            <>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: TEXT_DARK }}>
                {t("whatsappChat.needLogin")}
              </Typography>
              <Typography sx={{ fontFamily: FONT, color: TEXT_SECONDARY, fontSize: 14 }}>
                {t("whatsappChat.needLoginHint")}
              </Typography>
            </>
          )}
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell title={t("whatsappChat.title")} subtitle={instanceSubtitle} actions={headerActions}>
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 220px)",
          minHeight: 420,
          border: `1px solid ${BORDER}`,
          borderRadius: RADIUS_CARD,
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        {/* Chat list */}
        <Box sx={{ width: 320, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${BORDER}` }}>
          <Box sx={{ p: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              color="secondary"
              placeholder={t("whatsappChat.searchChats")}
              value={chatSearch}
              onChange={(e) => setChatSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: TEXT_SUBTLE }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
              {CHAT_FILTERS.map((f) => (
                <Chip
                  key={f}
                  size="small"
                  color="secondary"
                  variant={chatFilter === f ? "filled" : "outlined"}
                  label={t(`whatsappChat.filters.${f}`)}
                  onClick={() => changeChatFilter(f)}
                  sx={{ fontFamily: FONT, fontWeight: 600 }}
                />
              ))}
            </Box>
          </Box>
          <Divider />
          <Box
            sx={{ flex: 1, overflowY: "auto" }}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) loadMoreChats();
            }}
          >
            {loadingChats ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} color="secondary" />
              </Box>
            ) : filteredChats.length === 0 ? (
              <Typography sx={{ fontFamily: FONT, color: TEXT_SUBTLE, fontSize: 13, textAlign: "center", py: 4 }}>
                {t("whatsappChat.noChats")}
              </Typography>
            ) : (
              <List disablePadding>
                {filteredChats.map((chat) => (
                  <ListItemButton
                    key={chat.id}
                    selected={selectedChat?.id === chat.id}
                    onClick={() => openChat(chat)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setChatMenu({ chat, anchorPosition: { top: e.clientY, left: e.clientX } });
                    }}
                    sx={{
                      "&.Mui-selected": { bgcolor: PURPLE_TINT_100 },
                      "&.Mui-selected:hover": { bgcolor: PURPLE_TINT_100 },
                      "&:hover .wa-chat-menu": { opacity: 1 },
                    }}
                  >
                    <ListItemAvatar>
                      <ChatAvatar groupId={groupId} apiBase={apiBase} chat={chat} size={40} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 14, color: TEXT_DARK }}>
                          {chat.name}
                        </Typography>
                      }
                      secondary={
                        <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_SECONDARY }}>
                          {chat.lastMessage?.fromMe ? `${t("whatsappChat.you")}: ` : ""}
                          {previewText(chat.lastMessage?.body, t)}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", ml: 1 }}>
                      {chat.timestamp ? (
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: 11,
                            whiteSpace: "nowrap",
                            color: chat.unreadCount ? "#128c7e" : TEXT_SUBTLE,
                            fontWeight: chat.unreadCount ? 700 : 400,
                          }}
                        >
                          {moment.unix(chat.timestamp).format("DD/MM HH:mm")}
                        </Typography>
                      ) : null}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: 20 }}>
                        {chat.pinned && <PushPinIcon sx={{ fontSize: 14, color: TEXT_SUBTLE, transform: "rotate(45deg)" }} />}
                        {/* Unread, on the right in WhatsApp green (same as the mobile app): a count
                            for new messages, a plain dot for "marked as unread" (unreadCount -1). */}
                        {chat.unreadCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 20,
                              height: 20,
                              px: 0.75,
                              borderRadius: 10,
                              bgcolor: WA_GREEN,
                              color: "#fff",
                              fontFamily: FONT,
                              fontSize: 11,
                              fontWeight: 700,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
                          </Box>
                        )}
                        {chat.unreadCount < 0 && <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: WA_GREEN }} />}
                        {/* WhatsApp Web's hover ⌄ on a chat row → Mark as unread / Pin. */}
                        <IconButton
                          className="wa-chat-menu"
                          size="small"
                          aria-label={t("whatsappChat.chatOptions")}
                          onClick={(e) => {
                            e.stopPropagation();
                            setChatMenu({ chat, anchorEl: e.currentTarget });
                          }}
                          sx={{ p: 0, opacity: chatMenu?.chat?.id === chat.id ? 1 : 0, transition: "opacity .15s", color: TEXT_SUBTLE }}
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
            {loadingMoreChats && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={20} color="secondary" />
              </Box>
            )}
          </Box>
        </Box>

        {/* Message thread */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {!selectedChat ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: FONT, color: TEXT_SUBTLE }}>{t("whatsappChat.selectChat")}</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${BORDER}` }}>
                {searchOpen ? (
                  // Search bar: query · "2/5" · ▲ older · ▼ newer · ✕
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      color="secondary"
                      placeholder={t("whatsappChat.searchMessages")}
                      value={messageSearch}
                      onChange={(e) => setMessageSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setMatchIdx((i) => Math.min(i + 1, Math.max(0, searchMatches.length - 1)));
                        if (e.key === "Escape") closeSearch();
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: TEXT_SUBTLE }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {messageSearch.trim() && (
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEXT_SUBTLE, whiteSpace: "nowrap", px: 0.5 }}>
                        {searchMatches.length ? `${Math.min(matchIdx, searchMatches.length - 1) + 1}/${searchMatches.length}` : "0/0"}
                      </Typography>
                    )}
                    <IconButton color="secondary" onClick={() => setMatchIdx((i) => i + 1)} disabled={matchIdx >= searchMatches.length - 1} aria-label={t("whatsappChat.olderMatch")}>
                      <KeyboardArrowUpIcon />
                    </IconButton>
                    <IconButton color="secondary" onClick={() => setMatchIdx((i) => Math.max(0, i - 1))} disabled={matchIdx <= 0} aria-label={t("whatsappChat.newerMatch")}>
                      <KeyboardArrowDownIcon />
                    </IconButton>
                    <IconButton color="secondary" onClick={closeSearch} aria-label={t("common.close")}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      onClick={() => {
                        setInfoMember(null); // the chat's own info, not a sender's profile
                        setMediaPanel(null);
                        setInfoOpen(true);
                      }}
                      sx={{ display: "flex", alignItems: "center", gap: 1.25, cursor: "pointer", minWidth: 0, flex: 1 }}
                      title={selectedChat.isGroup ? t("whatsappChat.groupInfo") : t("whatsappChat.contactInfo")}
                    >
                      <ChatAvatar groupId={groupId} apiBase={apiBase} chat={selectedChat} size={40} />
                      <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 700, color: TEXT_DARK }}>{selectedChat.name}</Typography>
                    </Box>
                    <IconButton color="secondary" onClick={(e) => setHeaderMenu(e.currentTarget)} aria-label={t("whatsappChat.moreOptions")}>
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Menu anchorEl={headerMenu} open={!!headerMenu} onClose={() => setHeaderMenu(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <MenuItem
                  onClick={() => {
                    setHeaderMenu(null);
                    setMessageSearch("");
                    setSearchOpen(true);
                  }}
                >
                  <ListItemIcon>
                    <SearchIcon fontSize="small" />
                  </ListItemIcon>
                  {t("whatsappChat.searchMessages")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setHeaderMenu(null);
                    setInfoMember(null);
                    setMediaPanel(null);
                    setInfoOpen(true);
                  }}
                >
                  <ListItemIcon>
                    {selectedChat.isGroup ? <GroupIcon fontSize="small" /> : <ChatIcon fontSize="small" />}
                  </ListItemIcon>
                  {selectedChat.isGroup ? t("whatsappChat.groupInfo") : t("whatsappChat.contactInfo")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setHeaderMenu(null);
                    setMediaPanel({ fromInfo: false });
                  }}
                >
                  <ListItemIcon>
                    <ImageIcon fontSize="small" />
                  </ListItemIcon>
                  {t("whatsappChat.mediaLinksDocs")}
                </MenuItem>
              </Menu>

              <Box
                ref={messagesBoxRef}
                onScroll={(e) => {
                  if (e.currentTarget.scrollTop < 120) loadOlderMessages();
                }}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  bgcolor: BG_TINT,
                  // Chat wallpaper (one seamless tile, repeated) — stays put while the messages
                  // scroll over it.
                  backgroundImage: `url(${chatBackground})`,
                  backgroundSize: "204px 204px",
                  backgroundRepeat: "repeat",
                }}
              >
                {!loadingMessages && messages.length > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 0.5, minHeight: 32 }}>
                    {loadingOlder ? (
                      <CircularProgress size={20} color="secondary" />
                    ) : messagesHasMore ? (
                      <Button variant="outlined" color="secondary" size="small" onClick={loadOlderMessages}>
                        {t("whatsappChat.loadOlder")}
                      </Button>
                    ) : (
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_SUBTLE }}>
                        {t("whatsappChat.startOfChat")}
                      </Typography>
                    )}
                  </Box>
                )}
                {loadingMessages ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} color="secondary" />
                  </Box>
                ) : (
                  filteredMessages.map((m, i) => {
                    // For media messages `body` is the caption (backend sends the inline preview
                    // separately as `thumbnail`). Outgoing media echoed over the socket may carry
                    // the filename as body — don't repeat it under the file card.
                    const caption = m.hasMedia && m.body && m.body === m.filename ? null : m.body;

                    const isSelected = !!selectedIds?.has(m.id) || msgMenu?.m?.id === m.id;

                    return (
                    <Box
                      key={m.id || i}
                      data-msg-id={m.id || undefined}
                      // In selection mode a click anywhere on the row toggles it (and doesn't open media).
                      onClickCapture={
                        selecting && m.id
                          ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleSelect(m);
                            }
                          : undefined
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        borderRadius: 1.5,
                        cursor: selecting ? "pointer" : "default",
                        bgcolor: isSelected || highlightId === m.id ? "rgba(145,95,202,.12)" : "transparent",
                        transition: "background-color .4s",
                        "&:hover .wa-msg-menu": { opacity: 1 },
                      }}
                    >
                    {selecting && (
                      <Checkbox color="secondary" size="small" checked={!!selectedIds?.has(m.id)} sx={{ p: 0.5 }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", justifyContent: m.fromMe ? "flex-end" : "flex-start" }}>
                    <Box
                      onContextMenu={
                        !selecting && m.id && !m.revoked
                          ? (e) => {
                              e.preventDefault();
                              setMsgMenu({ m, anchorPosition: { top: e.clientY, left: e.clientX } });
                            }
                          : undefined
                      }
                      sx={{
                        position: "relative",
                        maxWidth: "70%",
                        // WhatsApp-style: a thin frame around photos / files / previews (text, sender
                        // name and time add their own side padding). A photo bubble is exactly as
                        // wide as the photo, so a long caption wraps under it instead of widening
                        // the bubble and leaving a coloured band beside the photo.
                        ...(m.hasMedia && m.mediaType === "image" ? { width: 288 } : {}),
                        bgcolor: m.fromMe ? PURPLE_MAIN : "#fff",
                        color: m.fromMe ? "#fff" : TEXT_DARK,
                        borderRadius: 2,
                        p: 0.5,
                        border: m.fromMe ? "none" : `1px solid ${BORDER}`,
                      }}
                    >
                      {!selecting && m.id && !m.revoked && (
                        // WhatsApp Web's hover ⌄ on a bubble → Copy / Forward.
                        <IconButton
                          className="wa-msg-menu"
                          size="small"
                          onClick={(e) => setMsgMenu({ m, anchorEl: e.currentTarget })}
                          aria-label={t("whatsappChat.messageOptions")}
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            zIndex: 1,
                            p: 0.25,
                            opacity: msgMenu?.m?.id === m.id ? 1 : 0,
                            transition: "opacity .15s",
                            color: m.fromMe ? "#fff" : TEXT_SUBTLE,
                            bgcolor: m.fromMe ? "rgba(0,0,0,.18)" : "rgba(255,255,255,.9)",
                            "&:hover": { bgcolor: m.fromMe ? "rgba(0,0,0,.28)" : "#fff" },
                          }}
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                      )}
                      {selectedChat.isGroup && !m.fromMe && m.author && (
                        <Typography
                          onClick={selecting ? undefined : () => openSenderProfile(m)}
                          sx={{
                            fontFamily: FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            color: PURPLE_MAIN,
                            mb: 0.25,
                            px: 1,
                            pt: 0.5,
                            cursor: selecting ? "inherit" : "pointer",
                            "&:hover": selecting ? undefined : { textDecoration: "underline" },
                          }}
                        >
                          {/* Saved contact name → real number → WhatsApp profile name → raw id */}
                          {m.authorName ||
                            (m.authorNumber ? `+${m.authorNumber}` : m.authorPushname ? `~${m.authorPushname}` : m.author)}
                        </Typography>
                      )}
                      {m.quoted && !m.revoked && (
                        <QuotedBlock
                          q={m.quoted}
                          chat={selectedChat}
                          onBubble
                          fromMe={m.fromMe}
                          onClick={selecting ? undefined : () => jumpToMessage(m.quoted.id)}
                          t={t}
                        />
                      )}
                      {m.hasMedia && (
                        <MediaContent m={m} groupId={groupId} apiBase={apiBase} chatId={selectedChat.id} t={t} onOpenImage={openImage} />
                      )}
                      {m.linkPreview && <LinkPreview preview={m.linkPreview} fromMe={m.fromMe} />}
                      {m.revoked && (
                        <Typography
                          sx={{ fontFamily: FONT, fontSize: 13.5, fontStyle: "italic", opacity: 0.8, px: 1, pt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          <BlockIcon sx={{ fontSize: 15 }} />
                          {m.fromMe ? t("whatsappChat.youDeletedMessage") : t("whatsappChat.messageDeleted")}
                        </Typography>
                      )}
                      {caption && (
                        <Typography sx={{ fontFamily: FONT, fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word", px: 1, pt: 0.5 }}>
                          {linkify(caption)}
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 10.5,
                          color: m.fromMe ? "rgba(255,255,255,.75)" : TEXT_SUBTLE,
                          textAlign: "right",
                          mt: 0.25,
                          px: 1,
                        }}
                      >
                        {m.edited && !m.revoked ? `${t("whatsappChat.edited")} · ` : ""}
                        {moment.unix(m.timestamp).format("HH:mm")}
                      </Typography>
                    </Box>
                    </Box>
                    </Box>
                    );
                  })
                )}
                {!loadingMessages &&
                  outgoing
                    .filter((o) => o.chatId === selectedChat.id)
                    .map((o) => (
                      <Box key={o.tempId} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                        {o.status === "failed" && (
                          <IconButton size="small" onClick={() => discardOutgoing(o)} aria-label={t("common.delete")}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Box
                          onClick={o.status === "failed" ? () => retryOutgoing(o) : undefined}
                          sx={{
                            maxWidth: "70%",
                            bgcolor: PURPLE_MAIN,
                            color: "#fff",
                            borderRadius: 2,
                            p: 0.5,
                            opacity: o.status === "sending" ? 0.85 : 1,
                            cursor: o.status === "failed" ? "pointer" : "default",
                          }}
                        >
                          {o.quoted && <QuotedBlock q={o.quoted} chat={selectedChat} onBubble fromMe t={t} />}
                          <Typography sx={{ fontFamily: FONT, fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word", px: 1, pt: 0.5 }}>
                            {linkify(o.body)}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, mt: 0.25, px: 1 }}>
                            {o.status === "failed" && (
                              <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: "#ffd2d2" }}>
                                {t("whatsappChat.notSentRetry")}
                              </Typography>
                            )}
                            <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: "rgba(255,255,255,.75)" }}>
                              {moment.unix(o.timestamp).format("HH:mm")}
                            </Typography>
                            {o.status === "sending" && <CircularProgress size={10} thickness={6} sx={{ color: "rgba(255,255,255,.85)" }} />}
                            {o.status === "sent" && <DoneIcon sx={{ fontSize: 14, color: "rgba(255,255,255,.85)" }} />}
                            {o.status === "failed" && <ErrorOutlineIcon sx={{ fontSize: 14, color: "#ffd2d2" }} />}
                          </Box>
                        </Box>
                      </Box>
                    ))}
              </Box>

              {editing && !selecting && (
                <Box sx={{ px: 1.5, pt: 1, borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1 }}>
                  <EditIcon sx={{ color: PURPLE_MAIN }} fontSize="small" />
                  <Box sx={{ flex: 1, minWidth: 0, borderLeft: `3px solid ${PURPLE_MAIN}`, bgcolor: BG_TINT, borderRadius: 1, px: 1, py: 0.5 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: PURPLE_MAIN }}>
                      {t("whatsappChat.editMessage")}
                    </Typography>
                    <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_SUBTLE }}>
                      {editing.body}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={cancelEdit} aria-label={t("common.cancel")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {replyTo && !selecting && (
                <Box sx={{ px: 1.5, pt: 1, borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1 }}>
                  <ReplyIcon sx={{ color: PURPLE_MAIN }} fontSize="small" />
                  <QuotedBlock q={replyTo} chat={selectedChat} onClick={() => jumpToMessage(replyTo.id)} t={t} />
                  <IconButton size="small" onClick={() => setReplyTo(null)} aria-label={t("whatsappChat.cancelReply")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {selecting ? (
              <Box sx={{ p: 1.5, borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton color="secondary" onClick={() => setSelectedIds(null)} aria-label={t("common.cancel")}>
                  <CloseIcon />
                </IconButton>
                <Typography sx={{ flex: 1, fontFamily: FONT, fontWeight: 600, color: TEXT_DARK }}>
                  {t("whatsappChat.selectedCount", { count: selectedIds.size })}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<ForwardIcon />}
                  onClick={() => {
                    setForwardError("");
                    setForwardOpen(true);
                  }}
                >
                  {t("whatsappChat.forward")}
                </Button>
              </Box>
              ) : voice.recording ? (
              <Box sx={{ p: 1.5, borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton color="error" onClick={() => voice.stop(false)} aria-label={t("whatsappChat.cancelRecording")}>
                  <DeleteIcon />
                </IconButton>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "error.main", animation: "waRecPulse 1s ease-in-out infinite", "@keyframes waRecPulse": { "50%": { opacity: 0.3 } } }} />
                  <Typography sx={{ fontFamily: FONT, fontWeight: 600, color: TEXT_DARK, fontVariantNumeric: "tabular-nums" }}>
                    {recTime(voice.elapsed)}
                  </Typography>
                </Box>
                <IconButton color="secondary" onClick={handleSendVoice} disabled={sending} aria-label={t("whatsappChat.send")}>
                  {sending ? <CircularProgress size={20} color="secondary" /> : <SendIcon />}
                </IconButton>
              </Box>
              ) : (
              <Box sx={{ p: 1.5, borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1, position: "relative" }}>
                {qrOpen && (
                  <QuickReplyPicker
                    replies={qrMatches}
                    query={qrQuery}
                    activeIndex={qrIndex}
                    loading={qrLoading && !quickReplies.length}
                    lang={qrLang}
                    onLangChange={setQrLang}
                    context={qrContext}
                    canEdit={canEditQuickReply}
                    onPick={pickQuickReply}
                    onHover={setQrIndex}
                    onEdit={(reply) => setQrEdit({ reply })}
                    onNew={() => setQrEdit({ reply: null })}
                    onClose={() => setQrDismissed(true)}
                    t={t}
                  />
                )}
                <input ref={fileInputRef} type="file" hidden multiple onChange={handleAttach} accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" />
                <IconButton color="secondary" onClick={() => fileInputRef.current?.click()} disabled={sending || !!editing} aria-label={t("whatsappChat.attachFile")}>
                  <AttachFileIcon />
                </IconButton>
                <IconButton
                  color="secondary"
                  onClick={() => {
                    setQrDismissed(false);
                    setMessageText("/");
                    messageInputRef.current?.focus();
                  }}
                  disabled={sending || !!editing}
                  aria-label={t("quickReplies.open")}
                  title={t("quickReplies.open")}
                >
                  <BoltIcon />
                </IconButton>
                <TextField
                  size="small"
                  fullWidth
                  color="secondary"
                  inputRef={messageInputRef}
                  onPaste={handlePaste}
                  placeholder={t("whatsappChat.typeMessage")}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => {
                    if (qrOpen) {
                      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                        e.preventDefault();
                        const n = qrMatches.length;
                        if (n) setQrIndex((i) => (i + (e.key === "ArrowDown" ? 1 : n - 1)) % n);
                        return;
                      }
                      if ((e.key === "Enter" && !e.shiftKey) || e.key === "Tab") {
                        e.preventDefault();
                        if (qrMatches[qrIndex]) pickQuickReply(qrMatches[qrIndex]);
                        return;
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        e.stopPropagation();
                        setQrDismissed(true);
                        return;
                      }
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={sending}
                />
                {messageText.trim() || editing ? (
                  <IconButton color="secondary" onClick={handleSend} disabled={!messageText.trim()} aria-label={t("whatsappChat.send")}>
                    {editing ? <DoneIcon /> : <SendIcon />}
                  </IconButton>
                ) : (
                  // Empty box → mic, like WhatsApp Web.
                  <IconButton color="secondary" onClick={handleMic} aria-label={t("whatsappChat.recordVoice")}>
                    <MicIcon />
                  </IconButton>
                )}
              </Box>
              )}
            </>
          )}
        </Box>

        {mediaPanel && selectedChat ? (
          <ChatMediaPanel
            groupId={groupId}
            apiBase={apiBase}
            chat={selectedChat}
            lang={i18n.language}
            onBack={mediaPanel.fromInfo ? () => setMediaPanel(null) : null}
            onClose={() => {
              setMediaPanel(null);
              setInfoOpen(false);
              setInfoMember(null);
            }}
            onOpenGallery={(slides, index) => setViewerImage({ slides, index })}
            t={t}
          />
        ) : infoOpen && selectedChat && (
          <ContactInfoPanel
            groupId={groupId}
            apiBase={apiBase}
            chat={selectedChat}
            onOpenMedia={() => setMediaPanel({ fromInfo: true })}
            initialMember={infoMember}
            onMessageMember={messageMember}
            onClose={() => {
              setInfoOpen(false);
              setInfoMember(null);
            }}
            onOpenImage={openImage}
            t={t}
          />
        )}
      </Box>
      <ImageViewer viewer={viewerImage} onClose={() => setViewerImage(null)} />
      <Menu
        open={!!chatMenu}
        onClose={() => setChatMenu(null)}
        anchorEl={chatMenu?.anchorEl || null}
        anchorReference={chatMenu?.anchorPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={chatMenu?.anchorPosition}
      >
        <MenuItem onClick={handleToggleUnread}>
          <ListItemIcon>
            {chatMenu?.chat?.unreadCount ? <MarkChatReadIcon fontSize="small" /> : <MarkChatUnreadIcon fontSize="small" />}
          </ListItemIcon>
          {chatMenu?.chat?.unreadCount ? t("whatsappChat.markRead") : t("whatsappChat.markUnread")}
        </MenuItem>
        <MenuItem onClick={handleTogglePin}>
          <ListItemIcon>
            <PushPinIcon fontSize="small" />
          </ListItemIcon>
          {chatMenu?.chat?.pinned ? t("whatsappChat.unpinChat") : t("whatsappChat.pinChat")}
        </MenuItem>
      </Menu>
      <Menu
        open={!!msgMenu}
        onClose={() => setMsgMenu(null)}
        anchorEl={msgMenu?.anchorEl || null}
        anchorReference={msgMenu?.anchorPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={msgMenu?.anchorPosition}
      >
        <MenuItem onClick={handleReply}>
          <ListItemIcon>
            <ReplyIcon fontSize="small" />
          </ListItemIcon>
          {t("whatsappChat.reply")}
        </MenuItem>
        {msgMenu?.m?.body ? (
          <MenuItem onClick={handleCopy}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            {t("whatsappChat.copy")}
          </MenuItem>
        ) : null}
        {msgMenu?.m?.hasMedia && msgMenu.m.mediaType === "image" ? (
          <MenuItem onClick={handleCopyImage}>
            <ListItemIcon>
              <ImageIcon fontSize="small" />
            </ListItemIcon>
            {t("whatsappChat.copyImage")}
          </MenuItem>
        ) : null}
        {msgMenu?.m?.hasMedia && (msgMenu.m.mediaType === "image" || msgMenu.m.mediaType === "video") ? (
          <MenuItem onClick={handleSaveMedia}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            {t("whatsappChat.saveMedia")}
          </MenuItem>
        ) : null}
        <MenuItem onClick={handleStartForward}>
          <ListItemIcon>
            <ForwardIcon fontSize="small" />
          </ListItemIcon>
          {t("whatsappChat.forward")}
        </MenuItem>
        {canEditMsg(msgMenu?.m) ? (
          <MenuItem onClick={handleStartEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            {t("whatsappChat.editMessage")}
          </MenuItem>
        ) : null}
        {canRevokeMsg(msgMenu?.m) ? (
          <MenuItem onClick={handleRevoke} sx={{ color: "error.main" }}>
            <ListItemIcon>
              <DeleteForeverIcon fontSize="small" color="error" />
            </ListItemIcon>
            {t("whatsappChat.deleteForEveryone")}
          </MenuItem>
        ) : null}
      </Menu>
      <ForwardDialog
        open={forwardOpen}
        groupId={groupId}
        apiBase={apiBase}
        count={selectedIds?.size || 0}
        sending={forwarding}
        error={forwardError}
        onClose={() => setForwardOpen(false)}
        onSend={handleForward}
        t={t}
      />
      <MediaComposer
        files={composerFiles}
        header={
          replyTo && selectedChat ? (
            <Box sx={{ px: 2, pb: 1, display: "flex", justifyContent: "center" }}>
              <Box sx={{ width: "100%", maxWidth: 720, bgcolor: "#fff", borderRadius: 1.5 }}>
                <QuotedBlock q={replyTo} chat={selectedChat} t={t} />
              </Box>
            </Box>
          ) : null
        }
        onCancel={() => setComposerFiles(null)}
        onSend={handleSendPhotos}
        t={t}
      />
      <QuickReplyEditDialog
        open={!!qrEdit}
        reply={qrEdit?.reply || null}
        onClose={() => setQrEdit(null)}
        onSave={handleSaveQuickReply}
        onDelete={handleDeleteQuickReply}
        t={t}
      />
      <QuickReplySendDialog
        open={!!qrSend}
        reply={qrSend?.reply || null}
        initialText={qrSend?.text || ""}
        onCancel={() => setQrSend(null)}
        onSend={handleSendQuickReply}
        t={t}
      />
      <AttachmentPreview
        file={pendingFile}
        sending={sending}
        error={pendingFileError}
        onCancel={() => setPendingFile(null)}
        onSend={handleSendAttachment}
        t={t}
      />
    </PageShell>
  );
};

export default WhatsAppChat;
